/*
* Association inventory detail view for authenticated users
*/

import { Upload } from '@progress/kendo-react-upload';
import * as _ from 'lodash';
import * as React from 'react';
import { Alert, Button, ButtonGroup, ButtonToolbar, Modal, OverlayTrigger, Pagination, Row, Tooltip, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import NavigationPrompt from 'react-router-navigation-prompt';
import { push } from 'react-router-redux';
import * as Treebeard from 'react-treebeard';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as Actions from '../actions/';
import * as MutateActions from '../actions/mutate';
import { AssociationDetailsCopyAddress } from '../association/components/association-details-copy-address';
import * as AppComp from '../common/app-component-base';
import { AppDataConstants } from '../common/app-data-constants';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import * as EvaluationQualityTier from '../common/evaluation-quality-tier';
import { Formatters } from '../common/formatters';
import { FormUtilities } from '../common/formutilities';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import * as ReduxQueries from '../queries';
import * as DetailFormCommon from './detail-form/common';
import { InventoryDetailFormComponentDetailsAssociation } from './detail-form/component-details-association';
import { InventoryDetailFormComponentDetailsLeft } from './detail-form/component-details-left';
import { DefaultPictureNotFoundUrl, DefaultPictureNotSelectedUrl, InventoryDetailFormComponentDetailsPictures } from './detail-form/component-details-pictures';
import { InventoryDetailFormComponentDetailsResearch } from './detail-form/component-details-research';
import { InventoryDetailFormComponentDetailsRight } from './detail-form/component-details-right';
import { InventoryDetailFormComponentDetailsShortcuts } from './detail-form/component-details-shortcuts';
import InventoryPhotoGallery from './gallery';
import EditComponentModal from '../component/edit-modal';
import Auth from '../auth/auth';
import { AuthConfig } from '../config/authconfiguration';
import * as LK from '../config/apilookupkeys';
import { statesList } from '../config/geolocations';

import 'react-dropdown/style.css';
import { isNullOrUndefined } from '../../src/utils/generalUtils';

const US_STATE_CODE_TO_NAME: Map<string, string> = new Map(
    statesList.map((s) => [s.stateCode, s.name] as [string, string])
);

const filterByRegion = (items: any[], activeRegionId: number | null): any[] => {
    if (!_.size(items)) { return []; }

    if (activeRegionId !== null) {
        const regionSpecific = _.filter(items, (item) => {
            const itemRegions = _.get(item, 'regions');
            return _.isArray(itemRegions) && _.includes(itemRegions, activeRegionId);
        });

        if (_.size(regionSpecific) > 0) {
            return regionSpecific;
        }
    }

    return _.filter(items, (item) => {
        const itemRegions = _.get(item, 'regions');
        return !itemRegions || !_.size(itemRegions);
    });
};

import './detail.css';
import './detail-form/detail-form.css';

export const TreeBeardStyle = {
    tree: {
        component: {
            width: '100%',
            display: 'block',
            verticalAlign: 'top',
            padding: '10px',
            '@media (max-width: 640px)': {
                width: '100%',
                display: 'block'
            }
        },

        base: {
            listStyle: 'none',
            // backgroundColor: '#21252B',
            margin: 0,
            padding: 0,
            // color: '#9DA5AB',
            // fontFamily: 'lucida grande ,tahoma,verdana,arial,sans-serif',
            fontSize: '14px'
        },

        node: {
            base: {
                position: 'relative'
            },

            link: {
                cursor: 'pointer',
                position: 'relative',
                padding: '0px 5px',
                display: 'block',
                color: '#263238',
            },

            activeLink: {
                color: '#2cabe3',
            },

            toggle: {
                base: {
                    position: 'relative',
                    display: 'inline-block',
                    verticalAlign: 'top',
                    marginLeft: '-5px',
                    height: '24px',
                    width: '24px'
                },

                wrapper: {
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    margin: '-7px 0 0 -7px',
                    height: '14px'
                },

                height: 14,
                width: 14,
                arrow: {
                    // fill: '#9DA5AB',
                    fill: '#263238',
                    strokeWidth: 0
                }
            },

            header: {
                base: {
                    display: 'inline-block',
                    verticalAlign: 'top',
                },

                connector: {
                    width: '2px',
                    height: '12px',
                    borderLeft: 'solid 2px black',
                    borderBottom: 'solid 2px black',
                    position: 'absolute',
                    top: '0px',
                    left: '-21px'
                },

                title: {
                    lineHeight: '24px',
                    verticalAlign: 'middle',
                    color: '#54667a',
                },

                titleActive: {
                    lineHeight: '24px',
                    verticalAlign: 'middle',
                    color: '#2cabe3',
                    fontWeight: '700',
                }
            },

            subtree: {
                listStyle: 'none',
                paddingLeft: '8px'
            },

            loading: {
                color: '#E2C089'
            }
        }
    }
};

export const DefaultCalculateCostsRoundTo = 25;

export const DefaultCostSourceId = 1;
export const ReplacementCostTypeIds = {
    BestCost: 1,
    WorstCost: 2,
};
export const ReplacementCostGroupIds = {
    'authorOnly': 0,
    'generalItems': 1,
};

export const DefaultNewReplacementCostGroupOptionValue = '1';

export interface IAssociationInventoryDetailProps extends AppComp.IAppComponentProps {
    association: any;
    associationId: number;
    duplicateAssociationInventory?: any;
    inventory: any;
    inventoryId: number;
    forceRequest?: any;
    newAssociationInventory?: any;
    newAssociationInventoryResearch?: any;
    deleteAssociationInventoryResearch?: any;
    deleteAssociationInventory?: any;
    searchAssociationInventory?: any;
    updateAssociationInventory?: any;
    viewAssociationInventory?: any;
}

export interface IAssociationInventoryDetailState extends AppComp.IAppComponentState {
    formChanges?: any;
    showModalUnsavedChanges: boolean;
    showModalDeleteInventory: boolean;
    showModalDeleteResearch: boolean;
    showModalReplacementCosts: boolean;
    showModalResearchComponents: boolean;
    showModalSelectPicture: boolean;
    showModalSelectPictureForPictureField: string;
    showModalSelectPictureFolder: string;
    showAddResearch: boolean;
    bypassAutoSaveChanges: boolean;
    nextInventoryLinkTo?: string;
    modalSelectPictureFiles?: Array<any>;
    selectedFilters: any;
    bestCostWorstCostDiscrepancy: boolean;
    inventory: any;
    showSaveToast: boolean;
    modalComponentObjId: string;
    isComponentLookupModalBusy: boolean;
    hasModalComponentUnsavedChanges: boolean;
    isComponentInformationEditing: boolean;
}

class AssociationInventoryDetail extends AppComp.AppComponent<any, IAssociationInventoryDetailState> {
    public originalFormDataCache = {};
    public saveChangesCallback: any = null;
    public deleteResearchId: number = 0;
    public pagedAwayFromRoute: boolean = false;
    public DefaultCategoryFilter: any = '1'; // '#DEFAULT#';

    public constructor(props: any) {
        super(props, 'app.association-inventory-detail.');

        this.state = this.initiateState(props);
    }

    public initiateState(props: Readonly<{ children?: React.ReactNode; }> & Readonly<any>) {
        const selectedFiltersCategory = Formatters.getStr(props, 'filterByCategory.categoryId', this.DefaultCategoryFilter);

        return {
            formChanges: {},
            showModalUnsavedChanges: false,
            showModalDeleteInventory: false,
            showModalDeleteResearch: false,
            showModalSelectPicture: false,
            showModalReplacementCosts: false,
            showModalResearchComponents: false,
            showModalSelectPictureForPictureField: '',
            showModalSelectPictureFolder: '',
            showAddResearch: false,
            bypassAutoSaveChanges: false,
            nextInventoryLinkTo: '',
            modalSelectPictureFiles: undefined,
            selectedFilters: {
                category: selectedFiltersCategory,
            },
            bestCostWorstCostDiscrepancy: false,
            inventory: props.inventory || null,
            showSaveToast: false,
            modalComponentObjId: '',
            isComponentLookupModalBusy: false,
            hasModalComponentUnsavedChanges: false,
            isComponentInformationEditing: false,
        };
    }

    public resetStateAfterSave() {
        this.originalFormDataCache = {};
        this.setState({ formChanges: {}, showModalUnsavedChanges: false, bestCostWorstCostDiscrepancy: false, showModalResearchComponents: false });
    }

    public componentDidUpdate(prevProps: Readonly<IAssociationInventoryDetailProps>): void {
        let resetFormData: boolean = this.getFormChangesResetRequired();
        if (resetFormData && _.size(this.state.formChanges)) {
            this.setState({ formChanges: {} });
        }

        const routeUnchanged = String(this.props.inventoryId) === String(prevProps.inventoryId);
        if (!routeUnchanged) {
            this.pagedAwayFromRoute = false;
        }

        if (this.props.inventory !== prevProps.inventory) {
            // Pagination swaps the displayed item with history.pushState, so the route (and the data request)
            // still point at the previous item. A refetch for that URL must not replace the item now displayed.
            // This only applies after paging; a real route change (new, duplicate, open from list) loads normally.
            const stateInventoryId = Number(_.get(this.state.inventory, 'inventoryId', 0));
            const incomingInventoryId = Number(_.get(this.props.inventory, 'inventoryId', 0));

            if (routeUnchanged && this.pagedAwayFromRoute && stateInventoryId && incomingInventoryId && stateInventoryId !== incomingInventoryId) {
                const refreshedItem = _.find(this.props.allInventory, (c) => Number(_.get(c, 'inventoryId', 0)) === stateInventoryId);
                if (refreshedItem) {
                    this.setState({ inventory: refreshedItem });
                }
            } else {
                this.setState({ inventory: this.props.inventory });
            }
        }
    }

    // Data transforms
    public extendInventoryData(invRecord: any, association: any) {
        // Extend grid row data
        if (!invRecord) { return {}; }

        let record = _.cloneDeep(invRecord);

        if (Formatters.getStr(record, 'componentName') && Formatters.getStr(record, 'componentName') !== Formatters.getStr(record, 'component.name')) {
            record.componentCustomName = Formatters.getStr(record, 'componentName');
        } else {
            record.componentCustomName = Formatters.getStr(record, 'component.name');
        }

        record.avgCost = 0.00;
        const sumCosts = _.get(record, 'bestCost', 0) + _.get(record, 'worstCost', 0);
        if (sumCosts > 0) {
            record.avgCost = Number((sumCosts / 2).toFixed(2));
        }

        return record;
    }

    public extendAssociationData(record: any) {
        if (!record) { return; }

        let result = _.cloneDeep(record);

        return result;
    }

    public resolveActiveRegionId(association: any, regions: any[]): number | null {
        if (!association) {
            return null;
        }

        // 1. Resolve active Client Region from association.client.regionId
        const clientRegionId = Formatters.getInt(association, 'client.regionId');
        if (clientRegionId && clientRegionId > 0) {
            return clientRegionId;
        }

        // 2. If empty/unavailable, resolve Association Region by mapping association.location.stateCode to a region in lookups.regions
        const stateCode = Formatters.getStr(association, 'location.stateCode');
        if (stateCode && _.size(regions)) {
            const code = stateCode.trim().toUpperCase();
            const stateName = US_STATE_CODE_TO_NAME.get(code);

            const matchedRegion: any = _.find(regions, (r: any) => {
                const regionName = (r.name || r.description || '').toLowerCase();
                if (stateName && regionName.includes(stateName.toLowerCase())) {
                    return true;
                }
                const tokens = regionName.split(/[^a-zA-Z0-9]+/).map((t: string) => t.toUpperCase());
                return tokens.includes(code);
            });

            if (matchedRegion) {
                return matchedRegion.regionId;
            }
        }

        return null;
    }

    public getSelectedEvaluationTierRow(formData: any) {
        const tierLabel = Formatters.getStr(formData, 'evaluationTier');
        const tierRows = _.get(formData, 'evaluationTierOptionValues', []);

        return _.find(tierRows, (t) => Formatters.getStr(t, 'evaluationTierLabel') === tierLabel);
    }

    public getEvaluationTextIsSavedVariant(formData: any, evaluationText: string) {
        const normalizedEvaluationText = Formatters.getStr({ evaluationText }, 'evaluationText').trim();
        if (!normalizedEvaluationText) { return true; }

        return _.some(
            _.get(formData, 'evaluationTextVariantOptions', []),
            (variant) => Formatters.getStr({ variant }, 'variant').trim() === normalizedEvaluationText
        );
    }

    public saveEvaluationTextVariantForFutureUse(formData: any, token: string) {
        const evaluationText = Formatters.getStr(formData, 'evaluation').trim();
        if (!Formatters.getBool(formData, 'saveEvaluationTextForFutureUse') ||
            !evaluationText ||
            this.getEvaluationTextIsSavedVariant(formData, evaluationText)) {
            return Promise.resolve();
        }

        const componentObjId = Formatters.getStr(formData, 'component.componentObjId');
        const componentId = Formatters.getInt(formData, 'component.componentId');
        const tierRow = this.getSelectedEvaluationTierRow(formData);
        const conditionLevel = Formatters.getInt(tierRow, 'conditionLevel');
        const groupId = Formatters.getInt(tierRow, 'sgGroupId', ReplacementCostGroupIds.generalItems);
        const groupCode = Formatters.getStr(tierRow, 'groupCode');
        const groupCodeDescriptor = Formatters.getStr(tierRow, 'groupCodeDescriptor');

        if (!componentObjId || !componentId || conditionLevel <= 0 || groupId < 0) {
            return Promise.resolve();
        }

        const templateObj = {
            _id: '_new_standard_evaluation',
            groupId,
            conditionLevel,
            groupCode,
            groupCodeDescriptor,
            evaluation: evaluationText,
            componentId,
        };

        const changeData = _.merge(
            {},
            { obj: templateObj },
            { componentObjId: componentObjId },
        );

        return this.props.permAddNewEvalCondition(changeData, token, false);
    }

    public extendComponentData(components: any, replacementCosts: any, groupComponents: any, authUserGroups: any) {
        if (!components || !_.size(components)) { return; }

        const activeRegionId = this.resolveActiveRegionId(this.props.association, _.get(this.props.lookups, 'regions', []));

        const sortedComponents = _.orderBy(components, ['componentId', 'asc']);

        const result = _.map(sortedComponents, (c) => {
            const componentId = _.get(c, 'componentId', 0);
            let resComponent = {
                componentId: componentId,
                name: _.get(c, 'name', ''),
                locations: _.get(c, 'locations', ['']),
                bestExplanationOptions: _.stubArray(),
                worstExplanationOptions: _.stubArray(),
                evaluationConditionLevel: '',
                evaluationConditionLevelOptions: _.stubArray(),
                evaluationConditionLevelAllOptions: _.stubArray(),
                evaluationConditionLevelAllGroups: authUserGroups,
                bestExplanationOptionsFull: _.stubArray(),
                worstExplanationOptionsFull: _.stubArray(),
            };

            if (!_.size(resComponent.locations)) { resComponent.locations = []; }

            // REPLACEMENT COSTS (Best/Worst Cost & Explanations)
            const compReplacementCosts = _.filter(replacementCosts, ['componentId', componentId]);
            if (_.size(compReplacementCosts)) {
                const rbAll = _.filter(compReplacementCosts, ['replacementCostType.replacementCostTypeId', ReplacementCostTypeIds.BestCost]);
                const rb = filterByRegion(rbAll, activeRegionId);
                if (_.size(rb)) {
                    resComponent.bestExplanationOptions = rb.map(e => _.get(e, 'explanation', ''));
                    resComponent.bestExplanationOptionsFull = rb.map(e => {
                        const groupId = _.get(e, 'groupId', ReplacementCostGroupIds.generalItems);
                        const authUserGroup = _.find(authUserGroups, { groupId: groupId });
                        const res = {
                            'text': Formatters.getStr(e, 'explanation'),
                            'authorOnly': (Formatters.getBool(e, 'authorOnly')) ? true : false,
                            '_id': _.get(e, '_id', ''),
                            'notes': Formatters.getStr(e, 'notes'),
                            'groupId': groupId,
                            'authUserGroup': authUserGroup,
                            'explanation': Formatters.getStr(e, 'explanation'),
                        };

                        return res;
                    });
                }

                const rwAll = _.filter(compReplacementCosts, ['replacementCostType.replacementCostTypeId', ReplacementCostTypeIds.WorstCost]);
                const rw = filterByRegion(rwAll, activeRegionId);
                if (_.size(rw)) {
                    resComponent.worstExplanationOptions = rw.map(e => _.get(e, 'explanation', ''));
                    resComponent.worstExplanationOptionsFull = rw.map(e => {
                        const groupId = _.get(e, 'groupId', ReplacementCostGroupIds.generalItems);
                        const authUserGroup = _.find(authUserGroups, { groupId: groupId });
                        const res = {
                            'text': Formatters.getStr(e, 'explanation'),
                            'authorOnly': (Formatters.getBool(e, 'authorOnly')) ? true : false,
                            '_id': _.get(e, '_id', ''),
                            'notes': Formatters.getStr(e, 'notes'),
                            'groupId': groupId,
                            'authUserGroup': authUserGroup,
                            'explanation': Formatters.getStr(e, 'explanation'),
                        };

                        return res;
                    });
                }
            }

            // EVALULATION & EVALULATIONSOURCE
            const compGroupComponents = _.filter(groupComponents, ['componentId', componentId]);
            if (_.size(compGroupComponents)) {
                const gcEvaluations = compGroupComponents.map(compSubGroupItem => {
                    const sgCompGroupId = Formatters.getInt(compSubGroupItem, 'groupId', ReplacementCostGroupIds.generalItems); // _.get(compSubGroupItem, 'groupId', ReplacementCostGroupIds.generalItems);
                    const authUserGroupIds = _.find(authUserGroups, { groupId: sgCompGroupId });

                    const compSubgroupEvaluations = _.get(compSubGroupItem, 'standardComponentEvaluations', []);
                    if (!_.size(compSubgroupEvaluations)) { return compSubgroupEvaluations; }

                    const filteredEvaluations = filterByRegion(compSubgroupEvaluations, activeRegionId);

                    return _.orderBy(filteredEvaluations, ['conditionLevel', 'standardEvaluationId'], ['asc', 'asc']).map(ev => {
                        const conditionLevel = Formatters.getInt(ev, 'conditionLevel');
                        const groupCode = Formatters.getStr(ev, 'groupCode');
                        const groupCodeDescriptor = Formatters.getStr(ev, 'groupCodeDescriptor');
                        const evaluationTierKey = EvaluationQualityTier.buildEvaluationTierKey(conditionLevel, groupCode, groupCodeDescriptor);
                        const evaluationTierLabel = EvaluationQualityTier.formatEvaluationTierLabel(conditionLevel, groupCode, groupCodeDescriptor);

                        const resSubgroupComp = {
                            componentId: componentId,
                            conditionLevel: conditionLevel,
                            evaluationId: Formatters.getInt(ev, 'standardEvaluationId'), // _.get(ev, 'standardEvaluationId', 0),
                            evaluationName: [ // [_.get(authUserGroupIds, 'description', ''), _.get(ev, 'conditionLevel', '')].join(' '),
                                Formatters.getStr(authUserGroupIds, 'description'),
                                Formatters.getStr(ev, 'conditionLevel'),
                                _.truncate(Formatters.getStr(ev, 'evaluation'), {
                                    'length': 70,
                                    'separator': /,? +/
                                })].join(' - '),
                            evaluationText: Formatters.getStr(ev, 'evaluation'),
                            sgGroupId: sgCompGroupId,
                            authUserGroupIds: authUserGroupIds,
                            groupCode,
                            groupCodeDescriptor,
                            evaluationTierKey,
                            evaluationTierLabel,
                        };

                        return resSubgroupComp;
                    });
                });

                resComponent.evaluationConditionLevelOptions = _.orderBy(_.flatten(gcEvaluations), ['conditionLevel', 'evaluationId'], ['asc', 'asc']);

                const lev = _.find(resComponent.evaluationConditionLevelOptions, { 'evaluationText': _.get(c, 'evaluation', '') });
                if (lev) { resComponent.evaluationConditionLevel = lev; }
            }

            return resComponent;
        });

        return result;
    }

    // Modal Button Handlers
    public handleIgnoreUnsavedChanges(e: any) {
        this.setState({ showModalUnsavedChanges: false });
        if (this.saveChangesCallback && _.isFunction(this.saveChangesCallback)) {
            this.saveChangesCallback();
        }
    }

    public handleSaveUnsavedChanges(e: any) {
        this.handleSaveButton(e);
    }

    public showUnsavedChangesModal(callerName: string, callback: any) {
        const hasUnsaved = this.getFormHasUnsavedChanges();

        if (hasUnsaved) {
            this.setState({ showModalUnsavedChanges: true, bypassAutoSaveChanges: true });
            this.saveChangesCallback = callback;
            this.handleSaveUnsavedChanges(null);
        } else {
            this.saveChangesCallback = null;

            if (callback && _.isFunction(callback)) {
                callback();
            }
        }

        return hasUnsaved;
    }

    public resetNewInventory(id: number) {
        const inventoryItem = _.find(this.props.allInventory, { inventoryId: id });

        if (inventoryItem) {
            this.setState({
                ...this.initiateState(this.props),
                inventory: inventoryItem
            });
        }
    }

    // Button handlers
    public async handlePaginationClick(e: any, inventoryIdTo: number, inventoryLinkTo: string) {
        // Check for unsaved then do action
        const hasUnsaved = this.getFormHasUnsavedChanges();
        if (hasUnsaved) {
            await this.saveInventory();
        }

        this.resetNewInventory(inventoryIdTo);
        this.pagedAwayFromRoute = true;
        window.history.pushState(null, '', inventoryLinkTo);
    }

    // The API resets the item's component name to the default whenever componentId is sent without a custom name,
    // so evaluation edits must also send the name currently shown.
    public getComponentIdentityForSave() {
        const fc = this.state.formChanges;
        const sendComponentId = _.get(fc, 'evaluationConditionLevel') || _.get(fc, 'evaluationTier') || _.get(fc, 'evaluation');
        if (!sendComponentId) {
            return { componentId: undefined };
        }

        const displayedName = Formatters.getStr(this.extendInventoryData(this.state.inventory, null), 'componentCustomName');
        return {
            componentId: _.get(this.state.inventory, 'componentId'),
            componentCustomName: displayedName ? displayedName : undefined,
        };
    }

    public handlePaginationClickAction(e: any, linkTo: string) {
        this.props.navigateTo(linkTo);
    }

    public handleShortcutLinkNavigation(e: any) {
        // check if unsaved changes. if not, then navigate to page
        const action = () => { this.handlePaginationClickAction(null, e); };

        // Check for unsaved, prompt for save, then do action
        this.showUnsavedChangesModal('handleShortcutLinkNavigation', action);
    }

    public getChangedInventory() {
        const token = _.get(this.props, 'authsession.access_token', '');
        const inventory = _.pick(this.state.inventory, ['inventoryId', '_id']);

        const changeData = _.merge(
            {},
            { association: _.pick(this.props.association, ['associationId', '_id']) },
            { inventory },
            this.getComponentIdentityForSave(),
            _.omit(this.state.formChanges, ['saveEvaluationTextForFutureUse'])
        );

        return {
            changeData,
            token
        };
    }

    public getCurrentFormData() {
        const {
            association,
            replacementCosts,
            components,
            componentCategories,
            groupComponents,
            costSources,
            authUserGroups,
            associationPhotos,
            associationPhotoFolders,
            associationPhotosTree,
        } = this.props;

        const assoc = this.extendAssociationData(association);
        const inv = this.extendInventoryData(this.state.inventory, assoc);
        const comps = this.extendComponentData(components, replacementCosts, groupComponents, authUserGroups);

        return this.getFormData(assoc, inv, comps, componentCategories, costSources, associationPhotos, associationPhotoFolders, associationPhotosTree, authUserGroups);
    }

    public handleSaveButton(e: any) {
        const formData = this.getCurrentFormData();
        const { changeData, token } = this.getChangedInventory();

        this.props.updateAssociationInventory(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.saveEvaluationTextVariantForFutureUse(formData, token).then(() => {
                    if (this.saveChangesCallback && _.isFunction(this.saveChangesCallback)) {
                        this.saveChangesCallback();
                    }

                    this.resetStateAfterSave();
                    this.props.forceRequest();
                });
            } else {
                // Error during creation...
            }
        });
    }

    public async saveInventory() {
        const formData = this.getCurrentFormData();
        const { changeData, token } = this.getChangedInventory();
        this.setState({ showModalUnsavedChanges: true, bypassAutoSaveChanges: true });
        await this.props.updateAssociationInventory(changeData, token, false);
        await this.saveEvaluationTextVariantForFutureUse(formData, token);
        this.resetStateAfterSave();
        this.props.forceRequest();
    }

    public handleCancelChangesButton(e: any) {
        this.saveChangesCallback = null;
        this.setState({ formChanges: {}, showModalUnsavedChanges: false, bestCostWorstCostDiscrepancy: false });
    }

    public handleDeleteButton(e: any, nextInventoryLinkTo: any) {
        this.setState({ showModalDeleteInventory: true, nextInventoryLinkTo: nextInventoryLinkTo, bypassAutoSaveChanges: true });
    }

    public handleDeleteButtonAction(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { association: _.pick(this.props.association, ['associationId', '_id']) },
            { inventory: _.pick(this.state.inventory, ['inventoryId', '_id']) },
        );

        const nextInventoryLinkTo = Formatters.getStr(this.state, 'nextInventoryLinkTo');

        this.props.deleteAssociationInventory(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.setState({ formChanges: {}, showModalDeleteInventory: false, nextInventoryLinkTo: '', bypassAutoSaveChanges: false });

                if (nextInventoryLinkTo) {
                    this.props.navigateTo(nextInventoryLinkTo);
                } else {
                    this.props.navigateTo(NavigationLinks.associationInventory(this.props.associationId));
                }
            } else {
                // Error during creation...
            }
        });
    }

    public handleNewButton(e: any) {
        // Check for pending changes
        const action = () => { this.handleNewButtonAction(e); };

        // Check for unsaved, prompt for save, then do action
        this.showUnsavedChangesModal('handleNewButton', action);
    }

    public handleNewButtonAction(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { association: _.pick(this.props.association, ['associationId', '_id']) },
            { inventory: _.pick(this.state.inventory, ['inventoryId', '_id']) }
        );

        this.props.newAssociationInventory(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.setState({ formChanges: {}, showModalUnsavedChanges: false });

                // FORCE RELOAD
                const newAssocId = _.get(response, 'body.associationId');
                const newInvId = _.get(response, 'body.inventoryId');

                this.props.navigateTo(NavigationLinks.associationInventoryDetail(newAssocId, newInvId));
            } else {
                // Error during creation...
            }
        });
    }

    public handleAddNewResearchButton(e: any) {
        // Check for pending changes
        const action = () => { this.handleAddNewResearchButtonAction(e); };

        // Check for unsaved, prompt for save, then do action
        // this.showUnsavedChangesModal('handleAddNewResearchButton', action);
        this.handleAddNewResearchButtonAction(e);
    }

    public handleAddNewResearchButtonAction(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { association: _.pick(this.props.association, ['associationId', '_id']) },
            { inventory: _.pick(this.state.inventory, ['inventoryId', '_id']) },
            { research: this.state.formChanges.addResearch }
        );

        this.props.newAssociationInventoryResearch(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};

                // FORCE RELOAD
                // const newAssocId = _.get(response, 'body.associationId');
                // const newInvId = _.get(response, 'body.inventoryId');

                // this.props.navigateTo(NavigationLinks.associationInventoryDetail(newAssocId, newInvId));
                let fc = _.cloneDeep(this.state.formChanges);
                _.unset(fc, 'addResearch');
                this.setState({ formChanges: fc });
            } else {
                // Error during creation...
            }
        });
    }

    public handleDeleteResearchButton(e: any, research: any) {
        this.deleteResearchId = _.get(research, 'researchId', 0);
        this.setState({ showModalDeleteResearch: true, bypassAutoSaveChanges: true });
    }

    public handleDeleteResearchButtonAction(e: any) { // , research: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { association: _.pick(this.props.association, ['associationId', '_id']) },
            { inventory: _.pick(this.state.inventory, ['inventoryId', '_id']) },
            { research: { 'researchId': this.deleteResearchId } }, // _.pick(research, ['researchId', '_id']) },
        );

        this.props.deleteAssociationInventoryResearch(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                // this.originalFormDataCache = {};

                // let fc = _.cloneDeep(this.state.formChanges);
                // _.unset(fc, 'addResearch');
                // this.setState({ formChanges: fc });
                this.setState({ showModalDeleteResearch: false, bypassAutoSaveChanges: false });
            } else {
                // Error during creation...
            }
        });
    }

    public handleDuplicateButton(e: any) {
        // Check for pending changes
        const action = () => { this.handleDuplicateButtonAction(e); };

        // Check for unsaved, prompt for save, then do action
        this.showUnsavedChangesModal('handleDuplicateButton', action);
    }

    public handleDuplicateButtonAction(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { association: _.pick(this.props.association, ['associationId', '_id']) },
            { inventory: _.pick(this.state.inventory, ['inventoryId', '_id']) },
        );

        this.props.duplicateAssociationInventory(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                this.setState({ formChanges: {}, showModalUnsavedChanges: false });

                // FORCE RELOAD
                const duplicateAssocId = _.get(response, 'body.associationId');
                const duplicateInvId = _.get(response, 'body.inventoryId');

                this.props.navigateTo(NavigationLinks.associationInventoryDetail(duplicateAssocId, duplicateInvId));
            } else {
                // Error during creation...
            }
        });
    }

    public handlePropertyButton(e: any) {
        // Check for pending changes
        const action = () => { this.handlePropertyButtonAction(e); };

        // Check for unsaved, prompt for save, then do action
        this.showUnsavedChangesModal('handlePropertyButton', action);
    }

    public handlePropertyButtonAction(e: any) {
        this.props.navigateTo(NavigationLinks.associationDetail(this.props.associationId));
    }

    public handleShowModalSelectPictureButton(e: any, pictureFieldName: string) {
        if (!pictureFieldName) { return; }
        this.setState({ showModalSelectPicture: true, showModalSelectPictureForPictureField: pictureFieldName });
    }

    public handleHideModalSelectPictureButton(e: any) {
        this.setState({ showModalSelectPicture: false, showModalSelectPictureForPictureField: '', showModalSelectPictureFolder: '' });
    }

    public handleHideAndSaveModalSelectPictureButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { association: _.pick(this.props.association, ['associationId', '_id']) },
            { inventory: _.pick(this.state.inventory, ['inventoryId', '_id']) },
            this.getComponentIdentityForSave(),
            this.state.formChanges
        );

        this.props.updateAssociationInventory(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                if (this.saveChangesCallback && _.isFunction(this.saveChangesCallback)) {
                    this.saveChangesCallback();
                }

                this.originalFormDataCache = {};
                this.setState({ formChanges: {}, showModalUnsavedChanges: false, bestCostWorstCostDiscrepancy: false, showModalSelectPicture: false, showModalSelectPictureForPictureField: '', showModalSelectPictureFolder: '' });
            } else {
                // Error during creation...
            }
        });
    }

    public handleInventoryButton(e: any, s: any) {
        // Check for pending changes
        const action = () => { this.handleInventoryButtonAction(e, s); };

        // Check for unsaved, prompt for save, then do action
        this.showUnsavedChangesModal('handleInventoryButton', action);
    }

    public handleInventoryButtonAction(e: any, s: any) {
        this.props.viewAssociationInventory(s);
        this.props.navigateTo(NavigationLinks.associationInventory(this.props.associationId));
    }

    public handleInventoryTemplateButton(e: any) {
        this.props.navigateTo(NavigationLinks.userInventoryTemplateEditObjId(this.props.userInventoryTemplate[0]._id));
    }

    public handleResearchComponentsButton(e: any) {
        // Check for pending changes
        const action = () => { this.handleResearchComponentsButtonAction(e); };

        // Check for unsaved, prompt for save, then do action
        this.showUnsavedChangesModal('handleResearchComponentsButton', action);
    }

    public handleResearchComponentsButtonAction(e: any) {
        this.props.searchAssociationInventory(Number(this.props.associationId));
        this.props.navigateTo(NavigationLinks.researchComponentsSearch());
    }

    public handleAddResearchButton(e: any) {
        // Check for pending changes
        const action = () => { this.handleAddResearchButtonAction(e); };

        // Check for unsaved, prompt for save, then do action
        this.showUnsavedChangesModal('handleAddResearchButton', action);
    }

    public handleAddResearchButtonAction(e: any) {
        this.setState({ showAddResearch: true });
        // this.props.navigateTo(NavigationLinks.researchComponentsSearch()); // (this.props.associationId));
    }

    public handleSaveReplacementCosts(e: any, formData: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { component: _.pick(this.state.inventory, ['componentId']) },
            {
                replacementCosts: _.pick(this.state.formChanges, [
                    'bestExplanationNew',
                    'bestExplanationNewNotes',
                    'bestExplanationNewGroup',

                    'worstExplanationNew',
                    'worstExplanationNewNotes',
                    'worstExplanationNewGroup',

                    'bestExplanationsAdd',
                    'bestExplanationsRemove',

                    'worstExplanationsAdd',
                    'worstExplanationsRemove'
                ])
            }
        );

        // console.log(`handleSaveReplacementCosts`, {
        //     changeData,
        //     formData,
        //     st_formChanges: this.state.formChanges,
        // });

        this.props.updateComponentReplacementCosts(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};

                // FORCE RELOAD
                this.handleCancelAndCloseModalReplacementCosts();
            } else {
                // Error during creation...
            }
        });
    }

    public handleTreeToggle(node: any, toggled: any) {
        this.setState({
            showModalSelectPictureFolder: Formatters.getStr(node, 'fullPath') || Formatters.getStr(node, 'name')
        });
    }

    public handleUploadBeforeUpload(e: any, spFullPath: any) {
        e.additionalData = {
            selectedPictureFolderFullPath: spFullPath,
        };

        this.setState({ modalSelectPictureFiles: e.newState });
    }

    public handleUploadAdd(e: any, spFullPath: any) {
        this.setState({ modalSelectPictureFiles: e.affectedFiles });
    }

    public handleUploadStatusChange(e: any, spFullPath: any) {
        // Reload data from api
        if (_.every(_.get(e, 'affectedFiles', []), ['status', 4])) {
            // all uploads suceeded, reload page
            this.props.forceRequest();
        } else {
            this.setState({ modalSelectPictureFiles: e.affectedFiles });
        }
    }

    public handleUploadBeforeRemove(e: any, spFullPath: any) {
        e.additionalData = {
            selectedPictureFolderFullPath: spFullPath,
        };
        this.setState({ modalSelectPictureFiles: e.newState });
    }

    public handleUploadRemove(e: any, spFullPath: any) {
        this.setState({ modalSelectPictureFiles: e.newState });
    }

    public handleUploadProgress(e: any, spFullPath: any) {
        this.setState({ modalSelectPictureFiles: e.newState });
    }

    public handleCalculateButton(e: any) {
        const {
            associationId,
            association,
            // inventoryId,
            // inventory,
            allInventory,
            replacementCosts,
            components,
            componentCategories,
            groupComponents,
            costSources,
            authUserGroups,
            associationPhotos,
            associationPhotoFolders,
            associationPhotosTree,
            ...rest
        } = this.props;

        const { inventory } = this.state;

        const assoc = this.extendAssociationData(association);
        const inv = this.extendInventoryData(inventory, assoc);
        const comps = this.extendComponentData(components, replacementCosts, groupComponents, authUserGroups);

        const formData = this.getFormData(assoc, inv, comps, componentCategories, costSources, associationPhotos, associationPhotoFolders, associationPhotosTree, authUserGroups);

        const bestExplanationValue = this.extractCostValueFromString(formData.bestExplanation, formData.bestExplanationOptions);
        const worstExplanationValue = this.extractCostValueFromString(formData.worstExplanation, formData.worstExplanationOptions);

        const bestPercentModifier = this.extractPercentValueFromString(formData.bestExplanation, formData.bestExplanationOptions);
        const worstPercentModifier = this.extractPercentValueFromString(formData.worstExplanation, formData.worstExplanationOptions);
        let singularPercentModifier = 1;
        if (bestPercentModifier !== 1) {
            singularPercentModifier = bestPercentModifier;
        } else if (worstPercentModifier !== 1) {
            singularPercentModifier = worstPercentModifier;
        }

        const qtyValue = this.extractValueFromString(formData.quantity);

        const bestCostRaw = Number(qtyValue * bestExplanationValue * singularPercentModifier) || 0;
        const worstCostRaw = Number(qtyValue * worstExplanationValue * singularPercentModifier) || 0;

        const bestCost = Number(Formatters.getCustomInventoryRoundToMultiple(bestCostRaw, DefaultCalculateCostsRoundTo));
        const worstCost = Number(Formatters.getCustomInventoryRoundToMultiple(worstCostRaw, DefaultCalculateCostsRoundTo));

        let hasChanges = false;
        let formChanges = this.state.formChanges;
        if (formChanges.bestCost !== bestCost) {
            _.set(formChanges, 'bestCost', bestCost);
            hasChanges = true;
        }

        if (formChanges.worstCost !== worstCost) {
            _.set(formChanges, 'worstCost', worstCost);
            hasChanges = true;
        }

        if (hasChanges) {
            this.setState({ formChanges: formChanges });
        }
    }

    public handleAddBestExplanationButton(e: any) {
        this.handleOpenComponentLookupEditorModal(e);
    }

    public handleAddWorstExplanationButton(e: any) {
        this.handleOpenComponentLookupEditorModal(e);
    }

    public getModalLookupEditorTargetComponentObjId(inventoryRecord: any = this.state.inventory) {
        const componentId = Formatters.getInt(inventoryRecord, 'componentId');
        if (componentId > 0) {
            const componentLookup = _.find(this.props.components, (c) => Formatters.getInt(c, 'componentId') === componentId);
            const componentObjIdFromLookup = Formatters.getStr(componentLookup, '_id');
            if (componentObjIdFromLookup) {
                return componentObjIdFromLookup;
            }
        }

        return Formatters.getStr(inventoryRecord, 'component._id');
    }

    public async saveInventoryBeforeComponentLookupEditorOpen() {
        if (!this.getFormHasUnsavedChanges()) {
            return true;
        }

        const { changeData, token } = this.getChangedInventory();

        try {
            const response = await this.props.updateAssociationInventory(changeData, token, false);
            const saveSucceeded = response && response.status >= 200 && response.status < 300;

            if (saveSucceeded) {
                this.resetStateAfterSave();
            }

            return saveSucceeded;
        } catch (err) {
            return false;
        }
    }

    public async handleOpenComponentLookupEditorModal(e: any) {
        const pendingInventoryRecord = _.merge({}, this.state.inventory, this.state.formChanges);
        const saveSucceeded = await this.saveInventoryBeforeComponentLookupEditorOpen();

        if (!saveSucceeded) {
            return;
        }

        const modalComponentObjId = this.getModalLookupEditorTargetComponentObjId(pendingInventoryRecord);
        if (!modalComponentObjId) {
            return;
        }

        this.setState({
            showModalReplacementCosts: true,
            modalComponentObjId: modalComponentObjId,
            isComponentLookupModalBusy: false,
            hasModalComponentUnsavedChanges: false,
            isComponentInformationEditing: false,
        });
    }

    public handleComponentLookupModalDirtyChange(isDirty: boolean) {
        if (this.state.hasModalComponentUnsavedChanges !== isDirty) {
            this.setState({ hasModalComponentUnsavedChanges: isDirty });
        }
    }

    public handleComponentInformationEditingChange(isEditing: boolean) {
        if (this.state.isComponentInformationEditing !== isEditing) {
            this.setState({ isComponentInformationEditing: isEditing });
        }
    }

    public handleNoteSave(e: any) {
        // console.log(`handleNoteSave`, e);
    }

    public handleShowResearchComponentsModal(e: any) {
        this.setState({ showModalResearchComponents: true });
    }

    public handleFormInputChange(e: any, x: any, formData?: any) {
        const getChangesResult = this.getFormChanges(e, x);

        if (getChangesResult.applyChanges) {
            if (this.state.showModalSelectPictureForPictureField === 'picture1') {
                this.setState({
                    formChanges: getChangesResult.formChanges,
                    showModalSelectPictureForPictureField: 'picture2'
                });
            } else {
                this.setState({
                    formChanges: getChangesResult.formChanges
                });
            }
            if (!isNullOrUndefined(e.target.props) && !isNullOrUndefined(e.target.props.name)) {
                if (e.target.props.name === 'bestCost' || e.target.props.name === 'worstCost') {
                    this.checkCostsDiscrepancy(e.value, e.target.props.name, formData);
                }
            }
        }
    }

    public handleNoteDelete(e: any, x: any) {
        const { reportNotes } = this.state.inventory;
        const getChangesResult = this.getFormChanges(e, x);

        if (reportNotes && reportNotes.length > 0) {
            const newReportNotes = reportNotes.filter(({ created }) => created !== e.target.value);

            this.setState({
                inventory: {
                    ...this.state.inventory,
                    reportNotes: newReportNotes
                },
                formChanges: getChangesResult.formChanges
            });
        }
    }

    public handleFormInputCustomEvaluation(e: any, x: any) {
        const curEvaluationText = Formatters.getStr(e, 'target.value') ? Formatters.getStr(e, 'target.value') : '';
        if (curEvaluationText === '') {
            console.warn('Failed to get updated evaluation text, defaulting to empty string');
        }
        const changes = [
            this.getFormChanges(
                {
                    target: {
                        props: {
                            name: 'evaluation'
                        }
                    },
                    value: curEvaluationText
                },
                null
            )
        ];
        const changesMapped: any = _.map(changes, 'formChangesResult');

        let cloneFormChanges = _.cloneDeep(this.state.formChanges);
        changesMapped.forEach((o) => _.merge(cloneFormChanges, o));

        this.setState({ formChanges: cloneFormChanges });
    }

    public handleEvaluationTierChange(e: any, x: any) {
        const {
            association,
            replacementCosts,
            components,
            componentCategories,
            groupComponents,
            costSources,
            authUserGroups,
            associationPhotos,
            associationPhotoFolders,
            associationPhotosTree,
            ...rest
        } = this.props;

        const { inventory } = this.state;

        const assoc = this.extendAssociationData(association);
        const inv = this.extendInventoryData(inventory, assoc);
        const comps = this.extendComponentData(components, replacementCosts, groupComponents, authUserGroups);

        const formData = this.getFormData(assoc, inv, comps, componentCategories, costSources, associationPhotos, associationPhotoFolders, associationPhotosTree, authUserGroups);

        let invChanges = _.stubArray();

        const newTierLabel = Formatters.getStr(e, 'target.value');
        const curTierLabel = Formatters.getStr(formData, 'evaluationTier');

        if (newTierLabel !== curTierLabel) {
            invChanges.push({ fieldName: 'evaluationTier', newValue: newTierLabel });

            const tierVals = _.get(formData, 'evaluationTierOptionValues', []);
            const tierObj = _.find(tierVals, (t) => Formatters.getStr(t, 'evaluationTierLabel') === newTierLabel);
            const allRows = _.get(formData, 'evaluationConditionLevelValues', []);
            const variants = tierObj
                ? _.filter(allRows, (r) => Formatters.getStr(r, 'evaluationTierKey') === Formatters.getStr(tierObj, 'evaluationTierKey'))
                : [];

            const curEvaluationText = Formatters.getStr(formData, 'evaluation');
            const stillValid = _.some(variants, (v) => Formatters.getStr(v, 'evaluationText') === curEvaluationText);
            const firstVariantText = Formatters.getStr(variants[0], 'evaluationText');
            const nextEvaluationText = stillValid ? curEvaluationText : firstVariantText;

            if (nextEvaluationText && nextEvaluationText !== curEvaluationText) {
                invChanges.push({ fieldName: 'evaluation', newValue: nextEvaluationText });
            }
        }

        if (_.size(invChanges)) {
            const changes = _.map(invChanges, (o) => {
                return this.getFormChanges(
                    {
                        target: {
                            props: {
                                name: _.get(o, 'fieldName')
                            }
                        },
                        value: _.get(o, 'newValue')
                    },
                    null
                );
            });

            const changesMapped: any = _.map(changes, 'formChangesResult');

            let cloneFormChanges = _.cloneDeep(this.state.formChanges);
            changesMapped.forEach((o) => _.merge(cloneFormChanges, o));

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handleEvaluationVariantChange(e: any, x: any) {
        const {
            association,
            replacementCosts,
            components,
            componentCategories,
            groupComponents,
            costSources,
            authUserGroups,
            associationPhotos,
            associationPhotoFolders,
            associationPhotosTree,
            ...rest
        } = this.props;

        const { inventory } = this.state;

        const assoc = this.extendAssociationData(association);
        const inv = this.extendInventoryData(inventory, assoc);
        const comps = this.extendComponentData(components, replacementCosts, groupComponents, authUserGroups);

        const formData = this.getFormData(assoc, inv, comps, componentCategories, costSources, associationPhotos, associationPhotoFolders, associationPhotosTree, authUserGroups);

        const newEvaluationText = Formatters.getStr(e, 'target.value');
        const curEvaluationText = Formatters.getStr(formData, 'evaluation');

        if (newEvaluationText !== curEvaluationText) {
            const change = this.getFormChanges(
                {
                    target: {
                        props: {
                            name: 'evaluation'
                        }
                    },
                    value: newEvaluationText
                },
                null
            );

            let cloneFormChanges = _.cloneDeep(this.state.formChanges);
            _.merge(cloneFormChanges, change.formChangesResult);

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handleUsefulLifeChange(e: any, x: any) {
        const {
            associationId,
            // inventoryId,
            association,
            // inventory,
            allInventory,
            replacementCosts,
            components,
            componentCategories,
            groupComponents,
            costSources,
            authUserGroups,
            associationPhotos,
            associationPhotoFolders,
            associationPhotosTree,
            ...rest
        } = this.props;

        const { inventory } = this.state;

        const assoc = this.extendAssociationData(association);
        const inv = this.extendInventoryData(inventory, assoc);
        const comps = this.extendComponentData(components, replacementCosts, groupComponents, authUserGroups);

        const formData = this.getFormData(assoc, inv, comps, componentCategories, costSources, associationPhotos, associationPhotoFolders, associationPhotosTree, authUserGroups);

        // Apply changes
        let invChanges = _.stubArray();

        const newUsefulLifeVal = Formatters.getStr(e, 'target.value');
        if (Formatters.getStr(formData, 'usefulLife') !== newUsefulLifeVal) {
            invChanges.push({ fieldName: 'usefulLife', newValue: newUsefulLifeVal });
        }

        if (newUsefulLifeVal === AppDataConstants.UsefulLifeOptions.NotApplicable) {
            invChanges.push({ fieldName: 'remainingUsefulLife', newValue: 0 });
            invChanges.push({ fieldName: 'bestCost', newValue: 0 });
            invChanges.push({ fieldName: 'bestExplanation', newValue: '' });
            invChanges.push({ fieldName: 'worstCost', newValue: 0 });
            invChanges.push({ fieldName: 'worstExplanation', newValue: '' });

            invChanges.push({ fieldName: 'costSource', newValue: '' });
        }

        if (_.size(invChanges)) {
            const changes = _.map(invChanges, (o) => {
                return this.getFormChanges(
                    {
                        target: {
                            props: {
                                name: _.get(o, 'fieldName')
                            }
                        },
                        value: _.get(o, 'newValue')
                    },
                    null
                );
            });

            const changesMapped: any = _.map(changes, 'formChangesResult');

            let cloneFormChanges = _.cloneDeep(this.state.formChanges);
            changesMapped.forEach((o) => _.merge(cloneFormChanges, o));

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handleCancelAndCloseModalUnsavedChanges() {
        this.saveChangesCallback = null;
        this.setState({ showModalUnsavedChanges: false });
    }

    public handleCancelAndCloseModalDeleteInventory() {
        this.setState({ showModalDeleteInventory: false, nextInventoryLinkTo: '', bypassAutoSaveChanges: false });
    }

    public handleCancelAndCloseModalDeleteResearch() {
        this.deleteResearchId = 0;
        this.setState({ showModalDeleteResearch: false, bypassAutoSaveChanges: false });
    }

    public handleCancelAndCloseModalReplacementCosts() {
        if (this.state.hasModalComponentUnsavedChanges) {
            const warnMessage = this.appText('prompt.unsaved-changes.body')
                || 'Warning: you have unsaved changes that will be lost if you continue. Press OK to abandon your changes, or press cancel to return to editing.';
            if (!window.confirm(warnMessage)) {
                return;
            }
        }

        let fc = this.state.formChanges;

        delete fc.bestExplanationsAdd;
        delete fc.bestExplanationsRemove;
        delete fc.bestExplanationNew;
        delete fc.bestExplanationNewNotes;
        delete fc.bestExplanationNewGroup;
        delete fc.worstExplanationsAdd;
        delete fc.worstExplanationsRemove;
        delete fc.worstExplanationNew;
        delete fc.worstExplanationNewNotes;
        delete fc.worstExplanationNewGroup;

        this.setState({
            showModalReplacementCosts: false,
            formChanges: fc,
            modalComponentObjId: '',
            isComponentLookupModalBusy: false,
            hasModalComponentUnsavedChanges: false,
            isComponentInformationEditing: false,
        });
    }

    public handleCancelAndCloseModalResearchComponents() {
        let origFC = _.cloneDeep(this.state.formChanges);
        this.setState({ showModalResearchComponents: false, formChanges: origFC });
    }

    public handleAddBestExplanationOption(e: any, formData: any) {
        let fc = this.state.formChanges;
        let updated = false;

        let bestExplanationsAdd = _.cloneDeep(_.get(formData, 'bestExplanationsAdd', []));
        let bestExplanationsRemove = _.cloneDeep(_.get(formData, 'bestExplanationsRemove', []));
        let explanation = Formatters.getStr(formData, 'bestExplanationNew');
        let explanationNotes = Formatters.getStr(formData, 'bestExplanationNewNotes');
        let explanationGroup = Formatters.getStr(formData, 'bestExplanationNewGroup');

        if (!_.includes(bestExplanationsAdd, explanation)) {
            bestExplanationsAdd.push(explanation);
            fc.bestExplanationsAdd = bestExplanationsAdd;
            fc.bestExplanationNew = '';
            updated = true;
        }

        if (_.includes(bestExplanationsRemove, explanation)) {
            fc.bestExplanationsRemove = _.reject(bestExplanationsRemove, (o) => o === explanation);
            updated = true;
        }

        if (updated) {
            this.setState({ formChanges: fc });
        }
    }

    public handleRemoveBestExplanationOption(e: any, formData: any, explanation: any) {
        let fc = this.state.formChanges;
        let updated = false;

        let bestExplanationsRemove = _.cloneDeep(_.get(formData, 'bestExplanationsRemove', []));
        let bestExplanationsAdd = _.cloneDeep(_.get(formData, 'bestExplanationsAdd', []));
        if (!_.includes(bestExplanationsRemove, explanation)) {
            bestExplanationsRemove.push(explanation);
            fc.bestExplanationsRemove = bestExplanationsRemove;
            updated = true;
        }

        if (_.includes(bestExplanationsAdd, explanation)) {
            fc.bestExplanationsAdd = _.reject(bestExplanationsAdd, (o) => o === explanation);
            updated = true;
        }

        if (updated) {
            this.setState({ formChanges: fc });
        }
    }

    public handleAddWorstExplanationOption(e: any, formData: any) {
        let fc = this.state.formChanges;
        let updated = false;

        let worstExplanationsAdd = _.cloneDeep(_.get(formData, 'worstExplanationsAdd', []));
        let worstExplanationsRemove = _.cloneDeep(_.get(formData, 'worstExplanationsRemove', []));
        let explanation = Formatters.getStr(formData, 'worstExplanationNew');

        if (!_.includes(worstExplanationsAdd, explanation)) {
            worstExplanationsAdd.push(explanation);
            fc.worstExplanationsAdd = worstExplanationsAdd;
            fc.worstExplanationNew = '';
            updated = true;
        }

        if (_.includes(worstExplanationsRemove, explanation)) {
            fc.worstExplanationsRemove = _.reject(worstExplanationsRemove, (o) => o === explanation);
            updated = true;
        }

        if (updated) {
            this.setState({ formChanges: fc });
        }
    }

    public handleRemoveWorstExplanationOption(e: any, formData: any, explanation: any) {
        let fc = this.state.formChanges;
        let updated = false;

        let worstExplanationsRemove = _.cloneDeep(_.get(formData, 'worstExplanationsRemove', []));
        let worstExplanationsAdd = _.cloneDeep(_.get(formData, 'worstExplanationsAdd', []));
        if (!_.includes(worstExplanationsRemove, explanation)) {
            worstExplanationsRemove.push(explanation);
            fc.worstExplanationsRemove = worstExplanationsRemove;
            updated = true;
        }

        if (_.includes(worstExplanationsAdd, explanation)) {
            fc.worstExplanationsAdd = _.reject(worstExplanationsAdd, (o) => o === explanation);
            updated = true;
        }

        if (updated) {
            this.setState({ formChanges: fc });
        }
    }

    // Helpers
    public getFormChanges = (e: any, x: any) => FormUtilities.getFormChanges(e, x, this.state.formChanges, this.originalFormDataCache);
    public extractValueFromString = (str: any, options: Array<any> = []): number => FormUtilities.extractValueFromString(str, options);
    /*
    public extractValueFromString (str: any, options: Array<any> = []): number {
        if (!String(str) && !_.size(options)) { return parseFloat('0'); }

        let strVal: any = '0';
        if (String(str)) {
            strVal = String(str);
        } else if (_.size(options)) {
            strVal = String(_.first(options));
        }

        return parseFloat(strVal.replace(/[^0-9.]/g, ''));
    }
    */

    public extractCostValueFromString(str: any, options: Array<any> = []): number {
        if (!String(str) && !_.size(options)) { return parseFloat('0'); }

        let strVal: any = '0';
        if (String(str)) {
            strVal = String(str);
        } else if (_.size(options)) {
            strVal = String(_.first(options));
        }

        // Was told on 11/30/18 that all explanation values with currencies will be formatted as [$curval]/etc.
        // '$60/a $70.12/b 20%'.split('/')[0].match(/[$]\d+(\.\d*)?/g);
        if (_.includes(strVal, '/') && _.includes(strVal, '$')) {
            const curSplit = strVal.split('/')[0];
            const curRep = curSplit.replace(/[^0-9.]/g, '');

            const currencyRegex = /\d*(\.\d*)?/g;
            const curMatches: Array<any> = curRep.match(currencyRegex);
            const curMatch = Formatters.getStr(curMatches, '[0]');
            return parseFloat(curMatch);
        } else {
            return parseFloat(strVal.replace(/[^0-9.]/g, ''));
        }
    }

    public extractPercentValueFromString(str: any, options: Array<any> = []): number {
        if (!String(str) && !_.size(options)) { return parseFloat('0'); }

        let strVal: any = '1';
        if (String(str)) {
            strVal = String(str);
        } else if (_.size(options)) {
            strVal = String(_.first(options));
        }

        if (_.includes(strVal, '%')) {
            const percentageRegex = /\d+(\.\d+)?[%]/g;
            const percentageMatches: Array<any> = strVal.match(percentageRegex);
            const percMatch = Formatters.getStr(percentageMatches, '[0]');

            return ((parseFloat(percMatch.replace(/[^0-9.]/g, ''))) / 100); // value/100 = pct
        } else {
            // default to 1: qty * cost * thisReturnAsAPercent
            return parseFloat('1');
        }
    }

    public buildLookupOptionsByFields(records: any, valueFieldName: string, textFieldName: string) {
        return (records && _.size(records)) ?
            records.map((c) => _.merge({}, {
                value: Formatters.getStr(c, valueFieldName),
                text: Formatters.getStr(c, textFieldName)
            })) : [];
    }

    public setStateSelectedFilters(filter: any) {
        if (!filter) { return; }

        this.setState({ selectedFilters: Object.assign({}, this.state.selectedFilters, filter) });
    }

    public handleSelectCategoryFilter(e: any) {
        this.setStateSelectedFilters({ category: e.value });
    }

    public checkCostsDiscrepancy(value: number, name: string, formData: any) {
        const fdBestCost = formData.bestCost;
        const fdWorstCost = formData.worstCost;

        if (isNullOrUndefined(fdBestCost) || isNullOrUndefined(fdWorstCost)) {
            return; // not yet set
        }
        // if (fdBestCost === 0 || fdWorstCost === 0) {
        //     return; // initialized but not yet defined
        // }
        if (name === 'bestCost') { // best cost was changed, is it half or less of the worst cost?
            if ((value <= (fdWorstCost / 2)) || (value >= (fdWorstCost * 2))) {
                this.setState({ bestCostWorstCostDiscrepancy: true });
            } else {
                this.setState({ bestCostWorstCostDiscrepancy: false });
            }
        } else {
            if ((value >= fdBestCost * 2) || (value <= fdBestCost / 2)) { // worst cost was changed, is it more double or more of the best cost?
                this.setState({ bestCostWorstCostDiscrepancy: true });
            } else {
                this.setState({ bestCostWorstCostDiscrepancy: false });
            }
        }
    }

    public getFormData(assoc: any, invIn: any, componentsTransformed: any, componentCategories: any, costSources: any, assocPhotos: any, assocPhotoFolders: any, assocPhotosTree: any, authUserGroups: any) {
        const inv = _.merge(invIn, this.state.formChanges);

        const compId = Formatters.getInt(inv, 'componentId');
        const compData = _.find(componentsTransformed, { componentId: compId });

        const componentsAll = _.map(_.get(this.props, 'components', []), (o) => {
            return {
                componentId: Formatters.getInt(o, 'componentId'),
                componentCategoryId: Formatters.getInt(o, 'componentCategoryId'),
            };
        });

        // Component Categories filter
        const componentCategoriesOptions = (_.size(componentCategories)) ?
            _.map(_.orderBy(componentCategories, ['categoryId'], ['asc']), (compCategory) => {
                let name = Formatters.getStr(compCategory, 'name');

                if (Formatters.getStr(compCategory, 'categoryId', '1') !== this.DefaultCategoryFilter) { name += ` (${Formatters.getInt(compCategory, 'categoryId')})`; }
                return {
                    value: Formatters.getStr(compCategory, 'categoryId', '1'),
                    label: name,
                    type: 'name',
                    name: Formatters.getStr(compCategory, 'name'),
                };
            }) : [];

        const selCatId = Formatters.getStr(this.state, 'selectedFilters.category', '1');
        const selectedComponentCategoryOption = _.find(componentCategoriesOptions, { 'value': selCatId });

        // Component Selection
        const groupedComponentOptions = (_.size(componentsAll) && _.size(componentsTransformed) && _.size(componentCategories)) ?
            _.compact(_.flattenDeep(_.orderBy(componentCategories, ['categoryId'], ['asc']).map((cat) => {
                const catId = Number(Formatters.getInt(cat, 'categoryId'));
                const catCompIds = _.filter(componentsAll, { 'componentCategoryId': catId });

                if (!_.size(catCompIds)) { return []; }

                const catComps = _.intersectionBy(componentsTransformed, catCompIds, 'componentId');
                const catIdStr = Formatters.getStr(cat, 'categoryId', this.DefaultCategoryFilter);
                const isFilteredCat = (catIdStr === selCatId || selCatId === this.DefaultCategoryFilter) ? true : false;

                if (!isFilteredCat) { return []; }

                let options = _.map(catComps, (catItem) => {
                    return {
                        value: Formatters.getInt(catItem, 'componentId'),
                        label: `${Formatters.getInt(catItem, 'componentId')} ${Formatters.getStr(catItem, 'name')}`,
                        type: 'name',
                        name: Formatters.getStr(catItem, 'name'),
                    };
                });

                options.unshift({
                    type: 'header',
                    label: Formatters.getStr(cat, 'name'),
                    value: catId * -1,
                    name: '',
                });

                return options;
            }))) : [];

        const selectedComponentOption = _.find(groupedComponentOptions, { 'value': compId });

        let avgCost = 0.00;
        const sumCosts = Formatters.getFloat(inv, 'bestCost') + Formatters.getFloat(inv, 'worstCost');
        if (sumCosts > 0) {
            avgCost = Number((sumCosts / 2).toFixed(2));
        }

        const invLocation = Formatters.getStr(inv, 'location');
        let locationOptions = _.get(compData, 'locations', []);
        if (!_.includes(locationOptions, invLocation)) { locationOptions.push(invLocation); }

        // const newBestGroupOptions = this.buildLookupOptionsByFields(streets, 'name', 'name');

        let result = {
            associationId: Formatters.getInt(assoc, 'associationId'),
            inventoryId: Formatters.getInt(inv, 'inventoryId'),

            components: componentsTransformed,
            costSources: costSources,

            component: {
                componentId: Formatters.getStr(inv, 'component.componentId'),
                componentCategoryId: Formatters.getStr(inv, 'component.componentCategoryId'),
                category: Formatters.getStr(inv, 'component.category'),
                componentObjId: _.get(inv, 'component._id'),
            },

            componentCategoriesOptions: componentCategoriesOptions,
            selectedComponentCategoryOption: selectedComponentCategoryOption,

            groupedComponentOptions: groupedComponentOptions,
            selectedComponentOption: selectedComponentOption,

            componentOptions: (_.size(componentsTransformed)) ?
                componentsTransformed.map((c) => _.merge(
                    {},
                    {
                        value: Formatters.getStr(c, 'componentId'),
                        text: `${_.get(c, 'componentId', '')} ${_.get(c, 'name', '')}`,
                    }))
                : [],

            componentCategoryOptions: (_.size(componentCategories)) ?
                componentCategories.map((c) => _.merge({}, { value: Formatters.getStr(c, 'categoryId'), text: Formatters.getStr(c, 'name') }))
                : [],

            componentCustomName: Formatters.getStr(inv, 'componentCustomName'),
            componentDecimal: Formatters.getStr(inv, 'componentDecimal', ''),
            location: invLocation,
            locationOptions: locationOptions,

            quantity: Formatters.getStr(inv, 'quantity'),
            quantityOptions: ['TODO'],

            clientQuestions: Formatters.getStr(inv, 'clientQuestions'),
            printClientQuestions: Formatters.getBool(inv, 'printClientQuestions'),
            // printClientQuestions: true,

            usefulLife: Formatters.getStr(inv, 'usefulLife'),
            usefulLifeOptions: [AppDataConstants.UsefulLifeOptions.NotApplicable, AppDataConstants.UsefulLifeOptions.OneTimeExpense], // [UsefulLifeOptions.NotApplicable, UsefulLifeOptions.OneTimeExpense],

            // remainingUsefulLife: Formatters.getInt(inv, 'remainingUsefulLife', undefined),
            remainingUsefulLife: _.get(inv, 'remainingUsefulLife', null),

            bestCost: Formatters.getFloat(inv, 'bestCost', 2),

            explanationGroupOptions: (_.size(authUserGroups)) ?
                authUserGroups.map((c) => _.merge({}, { value: Formatters.getStr(c, 'groupId'), text: Formatters.getStr(c, 'description') })) : [],

            bestExplanation: Formatters.getStr(inv, 'bestExplanation'),
            bestExplanationItem: { explanation: Formatters.getStr(inv, 'bestExplanation'), },
            bestExplanationOptions: _.cloneDeep(_.get(this.state.formChanges, 'bestExplanationOptions', _.get(compData, 'bestExplanationOptions', []))),
            bestExplanationOptionsFull: _.cloneDeep(_.get(this.state.formChanges, 'bestExplanationOptionsFull', _.get(compData, 'bestExplanationOptionsFull', []))),
            bestExplanationNew: Formatters.getStr(this.state.formChanges, 'bestExplanationNew', ''),
            bestExplanationNewNotes: Formatters.getStr(this.state.formChanges, 'bestExplanationNewNotes', ''),
            bestExplanationNewGroup: Formatters.getStr(this.state.formChanges, 'bestExplanationNewGroup', DefaultNewReplacementCostGroupOptionValue),
            bestExplanationsAdd: _.cloneDeep(_.get(this.state.formChanges, 'bestExplanationsAdd', [])),
            bestExplanationsRemove: _.cloneDeep(_.get(this.state.formChanges, 'bestExplanationsRemove', [])),

            worstCost: Formatters.getFloat(inv, 'worstCost', 2),

            worstExplanation: Formatters.getStr(inv, 'worstExplanation'),
            worstExplanationOptions: _.cloneDeep(_.get(this.state.formChanges, 'worstExplanationOptions', _.get(compData, 'worstExplanationOptions', []))),
            worstExplanationOptionsFull: _.cloneDeep(_.get(this.state.formChanges, 'worstExplanationOptionsFull', _.get(compData, 'worstExplanationOptionsFull', []))),
            worstExplanationNew: Formatters.getStr(this.state.formChanges, 'worstExplanationNew', ''),
            worstExplanationNewNotes: Formatters.getStr(this.state.formChanges, 'worstExplanationNewNotes', ''),
            worstExplanationNewGroup: Formatters.getStr(this.state.formChanges, 'worstExplanationNewGroup', DefaultNewReplacementCostGroupOptionValue),
            worstExplanationsAdd: _.cloneDeep(_.get(this.state.formChanges, 'worstExplanationsAdd', [])),
            worstExplanationsRemove: _.cloneDeep(_.get(this.state.formChanges, 'worstExplanationsRemove', [])),

            avgCost: Formatters.formatCurrencyIntl(avgCost, 2),
            costSource: Formatters.getStr(inv, 'costSource'),
            costSourceOptions: (_.size(costSources)) ? costSources.map(c => c.name) : [],

            evaluationConditionLevel: (() => {
                if (!compData) { return ''; }
                const invEvalText = Formatters.getStr(inv, 'evaluation');
                const evalOptions = _.get(compData, 'evaluationConditionLevelOptions', []);
                if (invEvalText && _.size(evalOptions)) {
                    const match = _.find(evalOptions, (o) => Formatters.getStr(o, 'evaluationText') === invEvalText);
                    if (match) { return Formatters.getStr(match, 'evaluationName'); }
                }
                const lev = _.get(compData, 'evaluationConditionLevel');
                return _.isObject(lev) ? Formatters.getStr(lev, 'evaluationName', '') : (lev || '');
            })(),
            evaluationConditionLevelOptions: (compData) ? _.get(compData, 'evaluationConditionLevelOptions', []).map(c => c.evaluationName) : [],
            evaluationConditionLevelValues: (compData) ? _.get(compData, 'evaluationConditionLevelOptions', []) : [],

            evaluationTierOptionValues: (() => {
                if (!compData) { return []; }
                const rows = _.get(compData, 'evaluationConditionLevelOptions', []);
                const tierRows = _.uniqBy(rows, 'evaluationTierKey');
                return _.orderBy(tierRows, ['conditionLevel', 'groupCode', 'groupCodeDescriptor'], ['asc', 'asc', 'asc']);
            })(),
            evaluationTierOptions: (() => {
                if (!compData) { return []; }
                const rows = _.get(compData, 'evaluationConditionLevelOptions', []);
                const tierRows = _.uniqBy(rows, 'evaluationTierKey');
                return _.map(_.orderBy(tierRows, ['conditionLevel', 'groupCode', 'groupCodeDescriptor'], ['asc', 'asc', 'asc']), 'evaluationTierLabel');
            })(),
            evaluationTier: (() => {
                const fcTier = Formatters.getStr(inv, 'evaluationTier');
                if (fcTier) { return fcTier; }
                if (!compData) { return ''; }
                const invEvalText = Formatters.getStr(inv, 'evaluation');
                const rows = _.get(compData, 'evaluationConditionLevelOptions', []);
                if (invEvalText && _.size(rows)) {
                    const match = _.find(rows, (o) => Formatters.getStr(o, 'evaluationText') === invEvalText);
                    if (match) { return Formatters.getStr(match, 'evaluationTierLabel'); }
                }
                const tierRows = _.uniqBy(rows, 'evaluationTierKey');
                const ordered = _.orderBy(tierRows, ['conditionLevel', 'groupCode', 'groupCodeDescriptor'], ['asc', 'asc', 'asc']);
                return Formatters.getStr(ordered[0], 'evaluationTierLabel', '');
            })(),
            evaluation: Formatters.getStr(inv, 'evaluation'),
            saveEvaluationTextForFutureUse: Formatters.getBool(inv, 'saveEvaluationTextForFutureUse'),
            reportNotesAdd: Formatters.getStr(inv, 'reportNotesAdd'),
            reportNotesDelete: _.get(inv, 'reportNotesDelete', []),
            reportNotes: _.get(inv, 'reportNotes', []),

            printNote: _.get(inv, 'printNote', false) ? true : false,
            complete: _.get(inv, 'complete', false) ? true : false,
            reviewed: _.get(inv, 'reviewed', false) ? true : false,

            subgroup: Formatters.getStr(inv, 'subgroup'),
            subgroupOptions: (_.size(assoc.inventorySubgroups)) ?
                assoc.inventorySubgroups.map((c) => _.merge({}, { value: Formatters.getStr(c, 'groupCode'), text: `${Formatters.getStr(c, 'groupCode')} - ${Formatters.getStr(c, 'title')}` }))
                : [],

            generalNote: Formatters.getStr(inv, 'generalNote'),
            // generalNotesAdd: Formatters.getStr(inv, 'generalNotesAdd'),
            // generalNotesDelete: _.get(inv, 'generalNotesDelete', []),
            // generalNotes: _.get(inv, 'generalNotes', []),

            pictures: _.get(inv, 'pictures', []),
            hideAssignedPictures: _.get(inv, 'hideAssignedPictures', _.get(this.state, 'hideAssignedPictures', true)),
            picture1: Formatters.getStr(inv, 'pictures[0]'),
            picture2: Formatters.getStr(inv, 'pictures[1]'),

            associationPhotos: (_.size(assocPhotos)) ? assocPhotos : [],
            associationPhotosTree: (_.size(assocPhotosTree)) ? assocPhotosTree : [],
            associationPhotoFolders: (_.size(assocPhotoFolders)) ? assocPhotoFolders : [],

            addResearch: {
                researchDate: _.get(inv, 'addResearch.researchDate'),
                projectDate: _.get(inv, 'addResearch.projectDate'),
                researchContactFirstName: Formatters.getStr(inv, 'addResearch.researchContactFirstName'),
                researchContactLastName: Formatters.getStr(inv, 'addResearch.researchContactLastName'),
                researchContactCompany: Formatters.getStr(inv, 'addResearch.researchContactCompany'),
                researchContactPhone: Formatters.getStr(inv, 'addResearch.researchContactPhone'),
                researchResearchedUL: Formatters.getInt(inv, 'addResearch.researchResearchedUL'),
                researchResearchedBestCost: Formatters.getFloat(inv, 'addResearch.researchResearchedBestCost'),
                researchResearchedWorstCost: Formatters.getFloat(inv, 'addResearch.researchResearchedWorstCost'),
                researchResearchNotes: Formatters.getStr(inv, 'addResearch.researchResearchNotes'),
            },

        };

        // Ensure best explanation values correct
        const invBestExplanation = Formatters.getStr(result, 'bestExplanation');
        if (!_.includes(result.bestExplanationOptions, invBestExplanation)) {
            result.bestExplanationOptions.push(invBestExplanation);

            result.bestExplanationOptionsFull.push({
                'text': invBestExplanation, // Formatters.getStr(e, 'explanation'),
                'authorOnly': false, // (Formatters.getBool(e, 'authorOnly')) ? true : false,
                '_id': '', // _.get(e, '_id', ''),
                'notes': [], // Formatters.getStr(e, 'notes'),
                'groupId': 0, // groupId,
                'authUserGroup': '', // authUserGroup,
                'explanation': invBestExplanation, // Formatters.getStr(e, 'explanation'),
            });
        }

        if (_.size(result.bestExplanationsAdd)) {
            result.bestExplanationOptions = _.concat(result.bestExplanationOptions, result.bestExplanationsAdd);
        }

        if (_.size(result.bestExplanationsRemove)) {
            result.bestExplanationOptions = _.difference(result.bestExplanationOptions, result.bestExplanationsRemove);
        }

        // Sort Best Explanations by value
        result.bestExplanationOptions = _.sortBy(result.bestExplanationOptions);
        result.bestExplanationOptionsFull = _.sortBy(result.bestExplanationOptionsFull, ['explanation']);

        // Ensure worst explanation values correct
        const invWorstExplanation = Formatters.getStr(result, 'worstExplanation');
        if (!_.includes(result.worstExplanationOptions, invWorstExplanation)) {
            result.worstExplanationOptions.push(invWorstExplanation);

            result.worstExplanationOptionsFull.push({
                'text': invWorstExplanation,
                'authorOnly': false,
                '_id': '',
                'notes': [],
                'groupId': 0,
                'authUserGroup': '',
                'explanation': invWorstExplanation,
            });
        }

        if (_.size(result.worstExplanationsAdd)) {
            result.worstExplanationOptions = _.concat(result.worstExplanationOptions, result.worstExplanationsAdd);
        }

        if (_.size(result.worstExplanationsRemove)) {
            result.worstExplanationOptions = _.difference(result.worstExplanationOptions, result.worstExplanationsRemove);
        }

        // Sort Worst Explanations by value
        result.worstExplanationOptions = _.sortBy(result.worstExplanationOptions);
        result.worstExplanationOptionsFull = _.sortBy(result.worstExplanationOptionsFull, ['explanation']);

        const invUsefulLife = Formatters.getStr(result, 'usefulLife');
        let usefulLifeOptions: Array<string> = _.get(result, 'usefulLifeOptions', []);
        if (!_.includes(usefulLifeOptions, invUsefulLife)) { usefulLifeOptions.push(invUsefulLife); }

        // Other
        if (!_.get(result, 'addResearch.researchDate')) {
            _.set(result, 'addResearch.researchDate', new Date());
        }

        if (!_.get(result, 'addResearch.projectDate')) {
            _.set(result, 'addResearch.projectDate', new Date());
        }

        if (_.size(result.reportNotes)) {
            result.reportNotes = _.filter(result.reportNotes, (o) => Formatters.getStr(o, 'notes') !== '');
            if (_.size(result.reportNotes)) {
                result.reportNotes = _.orderBy(result.reportNotes, ['created'], ['desc']);
            }
        }

        if (_.size(result.reportNotesDelete)) {
            result.reportNotes = _.omitBy(result.reportNotes, (o) => _.includes(result.reportNotesDelete, o.objectId));
        }

        const merged = _.merge(result, this.state.formChanges);

        const tierLabel = Formatters.getStr(merged, 'evaluationTier');
        const tierVals = merged.evaluationTierOptionValues || [];
        const tierObj = _.find(tierVals, (t) => Formatters.getStr(t, 'evaluationTierLabel') === tierLabel);
        const allRows = merged.evaluationConditionLevelValues || [];
        let evaluationTextVariantOptions: string[] = [];
        if (tierObj && Formatters.getStr(tierObj, 'evaluationTierKey')) {
            const tierKey = Formatters.getStr(tierObj, 'evaluationTierKey');
            evaluationTextVariantOptions = _.uniq(
                _.map(
                    _.filter(allRows, (r) => Formatters.getStr(r, 'evaluationTierKey') === tierKey),
                    (v) => Formatters.getStr(v, 'evaluationText')
                )
            );
        }
        merged.evaluationTextVariantOptions = evaluationTextVariantOptions;

        return merged;
    }

    public getFormChangesCount() {
        return _.size(this.state.formChanges);
    }

    public getFormHasUnsavedChanges() {
        if (this.getFormChangesCount() > 0) {
            return true;
        } else {
            return false;
        }
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;
        // Props values
        const pa = Formatters.getInt(this.props, 'associationId');
        const pi = Formatters.getInt(this.state.inventory, 'inventoryId');

        // Cached values
        const ca = Formatters.getInt(this.originalFormDataCache, 'associationId');
        const ci = Formatters.getInt(this.originalFormDataCache, 'inventoryId');

        // Determine if formdata state and cache needs reset due to changing associd or invid
        if (pa && pi && ca && ci) {
            resetFormData = (pa !== ca || pi !== ci) ? true : false;
        }

        return resetFormData;
    }

    public getPaginationLinks(allInventory: any, inventoryId: any, associationId: any) {
        let result = {
            prevInventoryId: 0,
            prevInventoryLinkTo: '',
            nextInventoryId: 0,
            nextInventoryLinkTo: '',
            activeLabel: '',
        };

        if (!_.size(allInventory) || !inventoryId || !associationId) {
            return result;
        }

        // const sortedInventory = _.sortBy(allInventory, ['subgroup', 'inventoryId']);
        const invItem = _.find(allInventory, (c) => Number(_.get(c, 'inventoryId', 0)) === Number(inventoryId));
        if (!invItem) { return result; }

        const invItemSubGroup = Formatters.getStr(invItem, 'subgroup');
        const groupedInventory = _.groupBy(allInventory, (c) => Formatters.getStr(c, 'subgroup'));
        const subgroups = Object.keys(groupedInventory).sort();
        const currentSubgroupItems = _.sortBy(groupedInventory[invItemSubGroup], ['componentId']);
        const currentSubgroupIndex = subgroups.indexOf(invItemSubGroup);

        const curInventoryIndex = _.findIndex(currentSubgroupItems, (c) => Number(_.get(c, 'inventoryId', 0)) === Number(inventoryId));

        // Prev Inventory
        let prevInventoryId;
        let prevInventoryLinkTo;
        if (curInventoryIndex > 0) {
            prevInventoryId = _.get(currentSubgroupItems[curInventoryIndex - 1], 'inventoryId', inventoryId);
        } else {
            const prevSubgroupKey = subgroups[(currentSubgroupIndex - 1 + subgroups.length) % subgroups.length];
            const prevSubgroupItems = groupedInventory[prevSubgroupKey];
            prevInventoryId = _.get(prevSubgroupItems[prevSubgroupItems.length - 1], 'inventoryId', inventoryId);
        }
        prevInventoryLinkTo = NavigationLinks.associationInventoryDetail(associationId, prevInventoryId);

        // Next Inventory
        let nextInventoryId;
        let nextInventoryLinkTo;
        if (curInventoryIndex < currentSubgroupItems.length - 1) {
            nextInventoryId = _.get(currentSubgroupItems[curInventoryIndex + 1], 'inventoryId', inventoryId);
        } else {
            const nextSubgroupKey = subgroups[(currentSubgroupIndex + 1) % subgroups.length];
            const nextSubgroupItems = groupedInventory[nextSubgroupKey];
            nextInventoryId = _.get(nextSubgroupItems[0], 'inventoryId', inventoryId);
        }

        nextInventoryLinkTo = NavigationLinks.associationInventoryDetail(associationId, nextInventoryId);

        const activeLabel = `${curInventoryIndex + 1} ${this.appTextCommon('buttons.of')} ${currentSubgroupItems.length} ${this.appText('in-subgroup')} ${invItemSubGroup}`;

        result.prevInventoryId = prevInventoryId;
        result.prevInventoryLinkTo = prevInventoryLinkTo;
        result.nextInventoryId = nextInventoryId;
        result.nextInventoryLinkTo = nextInventoryLinkTo;
        result.activeLabel = activeLabel;

        return result;
    }

    // Renderers
    public renderButtonToolbar = (allInventory: any, inventoryId: any, associationId: any) => {
        let buttonProps = {
            bsSize: 'sm',
        };

        let saveChangesButtonProps = {
            bsStyle: 'default',
            bsSize: 'sm',
            disabled: true,
            title: this.appTextCommon('buttons.no-changes'),
        };

        let cancelChangesButtonProps = {
            bsStyle: 'default',
            bsSize: 'sm',
            disabled: true,
            title: this.appTextCommon('buttons.cancel'),
        };

        let deleteButtonProps = {
            bsStyle: 'danger',
            bsSize: 'sm',
            disabled: false,
            title: this.appTextCommon('buttons.delete'),
        };

        const hasChanges = (this.getFormChangesCount() >= 1) ? true : false;

        if (hasChanges) {
            saveChangesButtonProps = {
                bsStyle: 'success',
                bsSize: 'sm',
                disabled: false,
                title: `${this.appTextCommon('buttons.save')}`,
            };

            cancelChangesButtonProps = {
                bsStyle: 'danger',
                bsSize: 'sm',
                disabled: false,
                title: `${this.appTextCommon('buttons.cancel')}`,
            };
        }

        const isTemplateAssociation = (associationId >= 900000) ? true : false;
        let inventoryTemplateButtonProps;

        if (isTemplateAssociation) {
            inventoryTemplateButtonProps = {
                bsStyle: 'success',
                bsSize: 'sm',
                disabled: false,
                title: `${this.appTextCommon('buttons.user-inventory-template')}`,
            };
        }

        const invItem = _.find(allInventory, (c) => Number(_.get(c, 'inventoryId', 0)) === Number(inventoryId));
        let invItemSubGroup = Formatters.getStr(invItem, 'subgroup');

        let pagination = (<div />);
        const paginationLinks = this.getPaginationLinks(allInventory, inventoryId, associationId);
        const disabled = (_.size(allInventory) === 1) ? true : false;
        if (_.size(allInventory) > 0) {

            pagination = (
                <Pagination bsSize="sm" className={(disabled) ? 'pagination-disabled' : ''}>
                    <li>
                        <a onClick={(e) => { e.preventDefault(); if (!disabled) { this.handlePaginationClick(e, paginationLinks.prevInventoryId, paginationLinks.prevInventoryLinkTo); } }} href="">
                            <i className="glyphicon glyphicon-chevron-left" />
                        </a>
                    </li>
                    <Pagination.Item className={(disabled) ? '' : 'active'}>{paginationLinks.activeLabel}</Pagination.Item>
                    <li>
                        <a onClick={(e) => { e.preventDefault(); if (!disabled) { this.handlePaginationClick(e, paginationLinks.nextInventoryId, paginationLinks.nextInventoryLinkTo); } }} href="">
                            <i className="glyphicon glyphicon-chevron-right" />
                        </a>
                    </li>
                </Pagination>
            );
        }

        return (
            <div>
                <Well className="well-sm">
                    <Row>
                        <Col xs={9}>
                            <ButtonToolbar>
                                { /* 20200128 - Show save button at all times. was: (hasChanges) ? */}
                                <Button {...saveChangesButtonProps} onClick={e => { this.handleSaveButton(e); }}><i className="glyphicon glyphicon-floppy-disk" /> {saveChangesButtonProps.title}</Button>
                                <Button {...cancelChangesButtonProps} onClick={e => { this.handleCancelChangesButton(e); }}><i className="glyphicon glyphicon-remove-circle" /> {cancelChangesButtonProps.title}</Button>

                                <Button {...deleteButtonProps} onClick={e => { this.handleDeleteButton(e, paginationLinks.nextInventoryLinkTo); }}><i className="glyphicon glyphicon-trash" /> {deleteButtonProps.title}</Button>

                                <Button {...buttonProps} bsStyle="info" onClick={e => { this.handleNewButton(e); }}>
                                    <i className="glyphicon glyphicon-plus" /> {this.appTextCommon('buttons.new')}
                                </Button>

                                <Button {...buttonProps} bsStyle="info" onClick={e => { this.handleDuplicateButton(e); }}>
                                    <i className="glyphicon glyphicon-copy" /> {this.appTextCommon('buttons.duplicate')}
                                </Button>

                                <Button {...buttonProps} bsStyle="info" onClick={e => { this.handleCalculateButton(e); }}>
                                    <i className="fa fa-calculator" /> {this.appText('buttons.calculate')}
                                </Button>

                                <Button bsSize="sm" bsStyle="info" onClick={(e) => { this.handlePropertyButton(e); }}>
                                    <i className="glyphicon glyphicon-eye-open" /> {this.appTextCommon('buttons.view-association')}
                                </Button>

                                <Button {...buttonProps} bsStyle="info" onClick={e => { this.handleInventoryButton(e, invItemSubGroup); }}>
                                    <i className="glyphicon glyphicon-th-list" /> {this.appTextCommon('buttons.view-inventory') /* this.appText('buttons.inventory')} */}
                                </Button>

                                <Button {...buttonProps} bsStyle="info" onClick={e => { this.handleResearchComponentsButton(e); }}>
                                    <i className="glyphicon glyphicon-tasks" /> {this.appTextCommon('buttons.view-research') /* this.appText('buttons.research-components')} */}
                                </Button>
                                {(isTemplateAssociation) ? <Button {...inventoryTemplateButtonProps} bsStyle="info" onClick={e => { this.handleInventoryTemplateButton(e); }}>
                                    <i className="glyphicon glyphicon-tasks" /> {this.appTextCommon('buttons.user-inventory-template') /* this.appText('buttons.research-components')} */}
                                </Button> : ''}
                            </ButtonToolbar>
                        </Col>
                        <div>
                            <div className="pagination-container">{pagination}</div>
                        </div>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderModalSelectPicture = (formData: any) => {
        const {
            associationId,
            // inventoryId,
            allInventory,
        } = this.props;

        const inventoryId = _.get(this.state.inventory, 'inventoryId', -1);

        const allImages = _.get(formData, 'associationPhotos', []);
        const allPicturesFromInventory = _.flattenDeep(_.map(allInventory, (inv) => _.flattenDeep(_.get(inv, 'pictures'))));

        let onlyShowUnusedPictures = _.get(formData, 'hideAssignedPictures');
        let availableImages = (onlyShowUnusedPictures) ? _.filter(allImages, (s) => { if (!_.includes(allPicturesFromInventory, s.pathDecoded)) { return s; } }) : allImages;

        const paginationLinks = this.getPaginationLinks(allInventory, inventoryId, associationId);

        const selectPictureField = Formatters.getStr(this.state, 'showModalSelectPictureForPictureField');
        const selectedPicturePath = Formatters.getStr(formData, selectPictureField);
        const picture1PictureField = 'picture1';
        const picture2PictureField = 'picture2';
        const picture1PicturePath = Formatters.getStr(formData, 'picture1');
        const picture2PicturePath = Formatters.getStr(formData, 'picture2');

        const selectedPictureFilename = _.last(selectedPicturePath.split('/'));
        const picture1Filename = _.last(picture1PicturePath.split('/'));
        const picture2Filename = _.last(picture2PicturePath.split('/'));

        const selectedPictureObj = _.find(allImages, (o) =>
            Formatters.getStr(o, 'pathDecoded') === selectedPicturePath ||
            Formatters.getStr(o, 'path') === selectedPicturePath
        );
        const picture1PictureObj = _.find(allImages, (o) =>
            Formatters.getStr(o, 'pathDecoded') === picture1PicturePath ||
            Formatters.getStr(o, 'path') === picture1PicturePath
        );
        const picture2PictureObj = _.find(allImages, (o) =>
            Formatters.getStr(o, 'pathDecoded') === picture2PicturePath ||
            Formatters.getStr(o, 'path') === picture2PicturePath
        );
        const selectedPictureFolderFromState = Formatters.getStr(this.state, 'showModalSelectPictureFolder');

        let treeBeardDataActive: any;

        const getPictureText = (index, name, path) => {
            return `${path}`;
        };

        // react-treebeard functionality
        const getTreeItemFile = (treeItem: any, treeItemKeyName: string = '', selectedPicPath: string = '', parentTreeItemKey: string = '') => {
            // if (!treeItem || treeItemKeyName || !selectedPicPath) { return null; }
            return null;

            /*
            const fileName = _.last(selectedPicPath.split('/'));
            const selectedPictureInFolder = (treeItemKeyName === fileName); // getSelectedPictureInTreeItemPath(treeItemKeyName, fileName, false);
 
            // console.log(`getTreeItemFile treeItemKeyName ${treeItemKeyName} fileName ${fileName} vs selectedPicPath ${selectedPicPath} result:`);
 
            if (selectedPictureInFolder) {
                const result = {
                    name: fileName,
                    active: selectedPictureInFolder,
                    children: [],
                };
 
                return result;
            }
 
            return null;
            */
        };

        const getTreeItemFolder = (treeItem: any, treeItemKey: string = '', selectedPicPath: string = '', treeItemFullPath: string = '') => {
            const isFolderActive =
                (selectedPictureFolder === treeItemFullPath) ||
                (selectedPictureFolder === treeItemKey) ||
                (normalizeFolderPath(selectedPictureFolder) === normalizeFolderPath(treeItemFullPath)) ||
                (normalizeFolderPath(selectedPictureFolder) === normalizeFolderPath(treeItemKey));

            const res = {
                name: treeItemKey,
                toggled: isFolderActive,
                active: isFolderActive,
                fullPath: treeItemFullPath,
                children: _.compact(getTreeItemChildren(treeItem, treeItemKey, selectedPicPath, treeItemFullPath))
            };

            if (isFolderActive) { treeBeardDataActive = res; }

            return res;
        };

        const getTreeItemChildren = (treeItem: any, treeItemName: string = '', selectedPicPath: string = '', parentTreeItemName: string = '') => {
            const treeItemType = _.get(treeItem, `treeItemType`, '');

            /* if (treeItemType === 'file') {
                return getTreeItemFile(treeItem, treeItemName, selectedPicPath);
            } else { */
            const treeItemChildrenKeys = getTreeItemChildrenKeys(treeItem, treeItemName);

            return _.flatten(_.map(treeItemChildrenKeys, (ticKey) => {
                const treeItemChild = _.get(treeItem, [ticKey]);
                const treeItemChildType = _.get(treeItemChild, `treeItemType`, 'folder');
                const treeItemFullPath = `${parentTreeItemName}/${treeItemName}/${ticKey}`;

                if (treeItemChildType === 'file') {
                    return getTreeItemFile(treeItemChild, ticKey, selectedPicPath, treeItemFullPath);
                } else {
                    return getTreeItemFolder(treeItemChild, ticKey, selectedPicPath, treeItemFullPath);
                }
            }));
            // }
        };

        const getTreeItemChildrenKeys = (treeItem: any, treeItemName: string = '') => {
            let childrenKeys: any = _.stubArray();
            if (treeItem && _.size(treeItem)) {
                childrenKeys = _.filter(_.keysIn(treeItem), (key) => {
                    if (key === 'treeItemType') { return false; }

                    const childVal = _.get(treeItem, [key]);
                    return _.isObject(childVal) && _.size(childVal) > 0;
                });
            }

            return childrenKeys;
        };

        const normalizeFolderPath = (folderPath: any = '') => {
            const normalized = String(folderPath || '')
                .replace(/^\/+/, '')
                .replace(/^Photos\/+/, '')
                .replace(/\/+$/, '');

            return normalized;
        };

        const getImageFolderPath = (img: any) => {
            const imagePathDecoded = Formatters.getStr(img, 'pathDecoded');
            if (imagePathDecoded && _.includes(imagePathDecoded, '/')) {
                const imagePathParts = imagePathDecoded.split('/');
                const imageFolderParts = _.dropRight(imagePathParts, 1);
                return normalizeFolderPath(imageFolderParts.join('/'));
            }

            return normalizeFolderPath(Formatters.getStr(img, 'parentFolder'));
        };

        // Treebeard
        const photoRootFolderKey: any = '/Photos';
        const rootFolderKeys: any = getTreeItemChildrenKeys(_.get(formData, 'associationPhotosTree'), 'rootFolderKeys');
        const rootFolderKey: any = _.first(rootFolderKeys);
        const rootFolderTreeItem: any = _.get(formData, ['associationPhotosTree', rootFolderKey]);
        const selectedPictureFolder =
            selectedPictureFolderFromState ||
            getImageFolderPath(selectedPictureObj) ||
            Formatters.getStr(selectedPictureObj, 'parentFolder') ||
            String(rootFolderKey || '');
        const isRootFolderActive =
            (selectedPictureFolder === rootFolderKey) ||
            (selectedPictureFolder === `${photoRootFolderKey}/${rootFolderKey}`) ||
            (normalizeFolderPath(selectedPictureFolder) === normalizeFolderPath(rootFolderKey));

        const treeBeardData = {
            name: rootFolderKey,
            toggled: true,
            active: isRootFolderActive,
            fullPath: `${photoRootFolderKey}/${rootFolderKey}`,
            children: _.compact(getTreeItemChildren(rootFolderTreeItem, rootFolderKey, selectedPicturePath, photoRootFolderKey))
        };

        if (isRootFolderActive) { treeBeardDataActive = treeBeardData; }

        const selectedPictureFolderFullPath = (treeBeardDataActive) ? Formatters.getStr(treeBeardDataActive, 'fullPath') : selectedPictureFolder;

        const treeBeardDecorators = _.merge({}, Treebeard.decorators, {
            Toggle: ({ style }) => {
                return (
                    <div style={style.base}>
                        <div style={style.wrapper} />
                    </div>
                );
            },

            Header: ({ style, node }) => {
                const iconType = (node.active) ? 'folder-open-o' : 'folder-o';
                const iconClass = `fa fa-${iconType}`;
                const iconStyle = { marginRight: '5px' };
                const divStyle = (node.active) ? style.titleActive : style.title;

                return (
                    <div style={style.base}>
                        <div style={divStyle}>
                            <i className={iconClass} style={iconStyle} />
                            {node.name}
                        </div>
                    </div>
                );
            }
        });

        const selectedPictureFolderNormalized = normalizeFolderPath(selectedPictureFolder);
        const filteredImages = (selectedPictureFolderNormalized) ?
            _.filter(availableImages, (img) => getImageFolderPath(img) === selectedPictureFolderNormalized)
            : availableImages;

        const galleryImages = filteredImages.map((p, ind) => {
            const path = Formatters.getStr(p, 'path');
            const pathDecoded = Formatters.getStr(p, 'pathDecoded');
            const fileName = (pathDecoded && _.includes(pathDecoded, '/')) ? _.last(pathDecoded.split('/')) : '';

            return {
                id: ind,
                src: ApiEndpoints.getInventoryPictureUrl(path, false),
                thumbnail: ApiEndpoints.getInventoryPictureUrl(path, true, 'w_100,h_75,c_fill,f_auto/'),
                thumbnailFallback: ApiEndpoints.getInventoryPictureUrl(DefaultPictureNotFoundUrl, true, 'w_100,h_75,c_fill,f_auto/'),

                caption: getPictureText(ind, _.get(formData, 'componentCustomName', ''), pathDecoded),
                orientation: 'square',
                title: fileName,
                pathDecoded: Formatters.getStr(p, 'pathDecoded'),
            };
        });

        // const { associationId, inventoryId, ...rest } = this.props;

        const uploadSaveUrl = ApiEndpoints.getUpdateAssociationInventoryUploadSaveUrlNew(associationId, inventoryId);
        const uploadRemoveUrl = ApiEndpoints.getUpdateAssociationInventoryUploadRemoveUrl(associationId, inventoryId);

        return (
            <Modal enforceFocus={false} bsSize="large" show={this.state.showModalSelectPicture} onHide={(e) => this.handleHideModalSelectPictureButton(e)}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.select-picture.title')}: {Formatters.getStr(formData, 'component.componentId')} - {Formatters.getStr(formData, 'componentCustomName')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Row>
                        <Col xs={12}>
                            <div className="modal-select-picture-folder-tree">
                                <Treebeard.Treebeard
                                    style={TreeBeardStyle}
                                    data={treeBeardData}
                                    onToggle={(node, toggled) => { this.handleTreeToggle(node, toggled); }}
                                    decorators={treeBeardDecorators}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col xs={5}>
                            <div className="modal-select-picture-current-photo">
                                <label>Picture 1</label>
                                {picture1PicturePath ? (
                                    <div>
                                        <div className="association-inventory-detail-picture-label detail-rendermodalselectpicture-picture-label">{picture1Filename}</div>
                                        <img className="img-responsive gallery-source img-rounded" src={ApiEndpoints.getInventoryPictureUrl(picture1PicturePath, true, 'w_300,h_188,c_fill,f_auto/')} />
                                        <div className="text-center">
                                            <a
                                                href="#"
                                                onClick={(e) => { this.setState({ showModalSelectPictureForPictureField: 'picture1' }); }}
                                                className="association-inventory-detail-picture-select-link">
                                                {this.appTextCommon('buttons.select')}
                                            </a>
                                        </div>

                                        <div className="text-center">
                                            <a
                                                href="#"
                                                onClick={(e) => { this.handleFormInputChange(_.merge({}, e, { 'target.props.name': 'picture1', 'value': '' }), null); }}
                                                className="association-inventory-detail-picture-link">
                                                {this.appTextCommon('buttons.clear')}
                                            </a>
                                        </div>
                                    </div>
                                ) :
                                    <div>
                                        <img className="img-responsive gallery-source img-rounded" src={ApiEndpoints.getInventoryPictureUrl(DefaultPictureNotSelectedUrl, true, 'w_300,h_188,c_fill,f_auto/')} />
                                        <div className="text-center">
                                            <a
                                                href="#"
                                                onClick={(e) => { this.setState({ showModalSelectPictureForPictureField: 'picture1' }); }}
                                                className="association-inventory-detail-picture-select-link">
                                                {this.appTextCommon('buttons.select')}
                                            </a>
                                        </div>
                                    </div>}
                            </div>
                        </Col>
                        <Col xs={5}>
                            <div className="modal-select-picture-current-photo">
                                <label>Picture 2</label>
                                {picture2PicturePath ? (
                                    <div>
                                        <div className="association-inventory-detail-picture-label detail-rendermodalselectpicture-picture-label">{picture2Filename}</div>
                                        <img className="img-responsive gallery-source img-rounded" src={ApiEndpoints.getInventoryPictureUrl(picture2PicturePath, true, 'w_300,h_188,c_fill,f_auto/')} />
                                        <div className="text-center">
                                            <a
                                                href="#"
                                                onClick={(e) => { this.setState({ showModalSelectPictureForPictureField: 'picture2' }); }}
                                                className="association-inventory-detail-picture-select-link">
                                                {this.appTextCommon('buttons.select')}
                                            </a>
                                        </div>

                                        <div className="text-center">
                                            <a
                                                href="#"
                                                onClick={(e) => { this.handleFormInputChange(_.merge({}, e, { 'target.props.name': 'picture2', 'value': '' }), null); }}
                                                className="association-inventory-detail-picture-link"
                                            >
                                                {this.appTextCommon('buttons.clear')}
                                            </a>
                                        </div>
                                    </div>
                                ) :
                                    <div>
                                        <img className="img-responsive gallery-source img-rounded" src={ApiEndpoints.getInventoryPictureUrl(DefaultPictureNotSelectedUrl, true, 'w_300,h_188,c_fill,f_auto/')} />
                                        <div className="text-center">
                                            <a
                                                href="#"
                                                onClick={(e) => { this.setState({ showModalSelectPictureForPictureField: 'picture2' }); }}
                                                className="association-inventory-detail-picture-select-link">
                                                {this.appTextCommon('buttons.select')}
                                            </a>
                                        </div>
                                    </div>}
                            </div>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <hr className="modal-select-picture-gallery-hr" />
                            {/* <div>
                                <span>{this.appText('modals.select-picture.upload-pictures-to-folder')} </span>
                                <Upload
                                    batch={true} // {false}
                                    multiple={true}
                                    defaultFiles={[]}
                                    withCredentials={false}
                                    onBeforeUpload={(e) => { this.handleUploadBeforeUpload(e, selectedPictureFolderFullPath); }}
                                    onBeforeRemove={(e) => { this.handleUploadBeforeRemove(e, selectedPictureFolderFullPath); }}
                                    onAdd={(e) => { this.handleUploadAdd(e, selectedPictureFolderFullPath); }}
                                    onRemove={(e) => { this.handleUploadRemove(e, selectedPictureFolderFullPath); }}
                                    onProgress={(e) => { this.handleUploadProgress(e, selectedPictureFolderFullPath); }}
                                    onStatusChange={(e) => { this.handleUploadStatusChange(e, selectedPictureFolderFullPath); }}
                                    files={this.state.modalSelectPictureFiles}
                                    saveUrl={uploadSaveUrl}
                                    removeUrl={uploadRemoveUrl}
                                />
                            </div> */}
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={5}>
                            <div className="inventory-detail-selected-pictures-show-assigned-toggle">
                                <KendoWrappers.KendoCheckbox
                                    name="hideAssignedPictures"
                                    label={this.appText('links.hide-images')}
                                    value={formData.hideAssignedPictures}
                                    onChange={(e, o) => this.handleFormInputChange(_.merge({}, e, { 'target.props.name': 'hideAssignedPictures', 'value': !formData.hideAssignedPictures }), o)}
                                    inputProps={{ colLabelXs: 4, colInputXs: 8, colLabelXl: 4, colInputXl: 4 }}

                                // lineHeight={1.5}
                                />
                            </div>
                        </Col>
                    </Row>

                    <hr className="modal-select-picture-gallery-hr" />
                    <div className="modal-select-picture-gallery">
                        <div className="modal-select-picture-gallery-title">
                            <span>{this.appText('modals.select-picture.displaying')} </span>
                            <span className="modal-select-picture-gallery-title-bold">{this.appTextPlural('modals.select-picture.picture', _.size(galleryImages), true, true)} </span>
                            {(selectedPictureFolderFullPath) ? (
                                <span>
                                    <span>{this.appText('modals.select-picture.in-folder')} </span>
                                    <span className="modal-select-picture-gallery-title-bold">{selectedPictureFolderFullPath}</span>
                                </span>
                            ) : undefined}
                        </div>
                        <InventoryPhotoGallery
                            images={galleryImages}
                            selectLinkCaption={this.appTextCommon('buttons.select')}
                            clearLinkCaption={this.appTextCommon('buttons.clear')}
                            onSelectLinkClick={(e, o) => { this.handleFormInputChange(_.merge({}, e, { 'target.props.name': selectPictureField, 'value': o.pathDecoded }), o); }}
                            onClearLinkClick={(e, o) => { this.handleFormInputChange(_.merge({}, e, { 'target.props.name': selectPictureField, 'value': '' }), o); }}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="info" onClick={(e) => this.handleHideModalSelectPictureButton(e)}>{'Close'}</Button>
                    <Button bsStyle="info" onClick={(e) => this.handleHideAndSaveModalSelectPictureButton(e)}>{'Save and Close'}</Button>
                    <Button bsStyle="info" onClick={(e) => this.handlePaginationClick(e, paginationLinks.nextInventoryId, paginationLinks.nextInventoryLinkTo)}>{'Save and Load Next Subgroup Item'}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderModalUnsavedChanges = () => {
        return (
            <Modal enforceFocus={false} show={this.state.showModalUnsavedChanges}>
                <Modal.Header closeButton={false}>
                    <Modal.Title>{this.appText('modals.save-changes.title')}</Modal.Title>
                </Modal.Header>
            </Modal>
        );
    }

    public renderModalDeleteInventory = (allInventory: any, inventoryId: any, associationId: any) => {
        return (
            <Modal enforceFocus={false} show={this.state.showModalDeleteInventory} onHide={() => this.handleCancelAndCloseModalDeleteInventory()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.delete.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.appText('modals.delete.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="danger" onClick={(e) => { this.handleDeleteButtonAction(e); }}>{this.appTextCommon('buttons.delete')}</Button>
                    <Button onClick={() => this.handleCancelAndCloseModalDeleteInventory()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderModalDeleteResearch = () => {
        return (
            <Modal enforceFocus={false} show={this.state.showModalDeleteResearch} onHide={() => this.handleCancelAndCloseModalDeleteResearch()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.delete-research.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.appText('modals.delete-research.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="danger" onClick={(e) => { this.handleDeleteResearchButtonAction(e); }}>{this.appTextCommon('buttons.delete') /* this.appText('modals.delete-research.delete') */}</Button>
                    <Button onClick={() => this.handleCancelAndCloseModalDeleteResearch()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderModalComponentResearch = (formData: any) => {
        const {
            associationId,
            // inventoryId,
            association,
            // inventory,
            inventoryResearch,
            allInventory,
            replacementCosts,
            components,
            componentCategories,
            groupComponents,
            costSources,
            authUserGroups,
            associationPhotos,
            associationPhotoFolders,
            associationPhotosTree,
            ...rest
        } = this.props;

        const { inventory } = this.state;
        const filteredInventoryResearch = _.filter(inventoryResearch, (ir) => ir.inventoryId === inventory.inventoryId);

        return (
            <Modal enforceFocus={false} dialogClassName="edit-inventory-research-component-history-modal-wrapper" show={this.state.showModalResearchComponents} onHide={() => this.handleCancelAndCloseModalResearchComponents()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.research-history.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <DetailFormCommon.InventoryDetailFormSection title={this.appText('modals.research-history.existing-history')}>
                        <div className="_edit-inventory-research-history-item">
                            <Row>
                                <Col xs={12}>
                                    <InventoryDetailFormComponentDetailsResearch
                                        appText={(e) => this.appText(e)}
                                        handleFormInputChange={(n, v) => this.handleFormInputChange(n, v)}
                                        handleSubmitForm={(e) => this.handleSaveButton(e)}
                                        handleSaveChanges={(e) => this.handleSaveButton(e)}
                                        handleNewResearchButton={(e) => this.handleAddNewResearchButton(e)}
                                        handleDeleteResearchButton={(e, r) => this.handleDeleteResearchButton(e, r)}
                                        formData={formData}
                                        inventoryResearch={filteredInventoryResearch}
                                        numFormChanges={this.getFormChangesCount()}
                                        defaultInputProps={{ colLabelXs: 4, colInputXs: 8 }}
                                        sectionTitle={this.appText('research-history')}
                                        showAdd={this.state.showAddResearch}
                                    />
                                </Col>
                            </Row>
                        </div>
                    </DetailFormCommon.InventoryDetailFormSection>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="info" onClick={() => this.handleCancelAndCloseModalResearchComponents()}>{this.appTextCommon('buttons.cancel')}</Button>
                    <Button bsStyle="success" onClick={(e) => this.handleSaveButton(e)}>{this.appTextCommon('buttons.save')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderModalReplacementCosts = (_formData: any) => {
        const modalComponentObjId = Formatters.getStr(this.state, 'modalComponentObjId');
        const modalBusy = this.state.isComponentLookupModalBusy;
        const hasUnsavedChanges = this.state.hasModalComponentUnsavedChanges;
        const isComponentInformationEditing = this.state.isComponentInformationEditing;
        const closeDisabled = modalBusy || hasUnsavedChanges || isComponentInformationEditing;
        const closeTooltip = hasUnsavedChanges
            ? this.appText('modals.replacement-costs.unsaved-changes')
            : this.appText('modals.replacement-costs.finish-editing-before-close');
        const auth = new Auth();
        const canDeleteStandardEvaluation = auth.isAuthenticated() && (
            auth.userHasRole(AuthConfig.UserRoles.administrator) ||
            auth.userHasRole(AuthConfig.UserRoles.regionalComponentListManager)
        );

        return (
            <Modal
                enforceFocus={false}
                backdrop={closeDisabled ? 'static' : true}
                keyboard={!closeDisabled}
                dialogClassName="edit-inventory-replacement-costs-item-modal-wrapper"
                show={this.state.showModalReplacementCosts}
                onHide={() => {
                    if (!closeDisabled) { this.handleCancelAndCloseModalReplacementCosts(); }
                }}
            >
                <Modal.Header closeButton={!closeDisabled}>
                    <Modal.Title>Component Lookup Editor</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {(modalComponentObjId) ? (
                        React.createElement(EditComponentModal as any, {
                            componentObjId: modalComponentObjId,
                            authsession: this.props.authsession,
                            canDeleteStandardEvaluation,
                            onModalDirtyChange: (isDirty: boolean) => this.handleComponentLookupModalDirtyChange(isDirty),
                            onComponentInformationEditingChange: (isEditing: boolean) => this.handleComponentInformationEditingChange(isEditing),
                        })
                    ) : (
                        <DetailFormCommon.InventoryDetailFormSection title="">
                            <div className="text-muted">
                                Unable to resolve component object id for modal editor.
                            </div>
                        </DetailFormCommon.InventoryDetailFormSection>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    {closeDisabled ? (
                        <OverlayTrigger placement="top" overlay={<Tooltip id="component-lookup-editor-close-tooltip">{closeTooltip}</Tooltip>}>
                            <span style={{ display: 'inline-block' }} tabIndex={0} aria-label={closeTooltip}>
                                <Button disabled={true} title={closeTooltip} aria-label={this.appTextCommon('buttons.close')}>
                                    {this.appTextCommon('buttons.close')}
                                </Button>
                            </span>
                        </OverlayTrigger>
                    ) : (
                        <Button onClick={() => this.handleCancelAndCloseModalReplacementCosts()}>
                            {this.appTextCommon('buttons.close')}
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>
        );
    }

    public render() {
        const {
            associationId,
            // inventoryId,
            association,
            // inventory,
            inventoryResearch,
            allInventory,
            replacementCosts,
            components,
            componentCategories,
            groupComponents,
            costSources,
            authUserGroups,
            associationPhotos,
            associationPhotoFolders,
            associationPhotosTree,
            ...rest
        } = this.props;

        const { inventory } = this.state;
        const inventoryId = _.get(inventory, 'inventoryId', -1);

        const allInventoryCheckSubgroupForPadding = _.map(allInventory, (each) => (_.isFinite(_.parseInt(each.subgroup)) && _.inRange(_.parseInt(each.subgroup), 1, 10)) ? FormUtilities.padSubgroup(each) : each);
        const allInventorySorted = _.sortBy(allInventoryCheckSubgroupForPadding, ['subgroup', 'componentId', 'componentDecimal', 'componentName']);

        const assoc = this.extendAssociationData(association);
        const inv = this.extendInventoryData(inventory, assoc);
        const comps = this.extendComponentData(components, replacementCosts, groupComponents, authUserGroups);

        const auth = new Auth();
        const isRegionalComponentManager = auth.isAuthenticated() && auth.userHasRole(AuthConfig.UserRoles.regionalComponentListManager);

        const pageTitle = this.appTextPageTitleAppendString(_.get(assoc, 'name', ''), 'page-title', true);

        let page = (<div />);

        if (Number(_.get(assoc, 'associationId', -1)) === Number(associationId)
            && Number(inventoryId) !== -1
            && Number(_.get(inv, 'inventoryId', -1) === Number(inventoryId))) {
            const formData = this.getFormData(assoc, inv, comps, componentCategories, costSources, associationPhotos, associationPhotoFolders, associationPhotosTree, authUserGroups);

            if (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache)) {
                this.originalFormDataCache = _.cloneDeep(formData);
            }

            const hasChanges = (this.getFormChangesCount() > 0) ? true : false;
            const bypassAutoSave = (this.state.bypassAutoSaveChanges === true) ? true : false;

            const invItem = _.find(allInventorySorted, (c) => Number(_.get(c, 'inventoryId', 0)) === Number(inventoryId));
            let invItemSubGroup = Formatters.getStr(invItem, 'subgroup');
            const invSubgroupItems = _.filter(allInventorySorted, (c) => Formatters.getStr(c, 'subgroup') === invItemSubGroup);

            let warnCostsDiscrepancy = (<div />);
            if (this.state.bestCostWorstCostDiscrepancy) {
                warnCostsDiscrepancy = (
                    <div className="inv-detail-costs-discrepancy-warning">
                        <Alert bsStyle="warning">
                            <h4><i className="fa fa-exclamation-triangle" /> {this.appText('costs-discrepancy.warning.title')}</h4>
                            <p>{this.appText('costs-discrepancy.warning.body')}</p>
                        </Alert>
                    </div>
                );
            }

            page = (
                <div>
                    {this.renderButtonToolbar(allInventorySorted, inventoryId, associationId)}
                    {this.renderModalUnsavedChanges()}
                    {this.renderModalDeleteInventory(allInventorySorted, inventoryId, associationId)}
                    {this.renderModalDeleteResearch()}
                    {this.renderModalSelectPicture(formData)}
                    {this.renderModalReplacementCosts(formData)}
                    {this.renderModalComponentResearch(formData)}

                    <NavigationPrompt
                        when={(curLocation, nextLocation) => {
                            const pathMismatch = (_.get(curLocation, 'pathname') !== _.get(nextLocation, 'pathname', '')) ? true : false;
                            const result = (hasChanges && pathMismatch && !bypassAutoSave) ? true : false;

                            return result;
                        }}
                    >
                        {({ isActive, onCancel, onConfirm }: { isActive: boolean, onCancel: () => void, onConfirm: () => void }) => {
                            if (isActive) {
                                return (
                                    <Modal enforceFocus={false} show={true} onHide={() => this.handleCancelAndCloseModalUnsavedChanges()} className="test">
                                        <Modal.Header closeButton={true}>
                                            <Modal.Title>{this.appText('modals.save-changes.title')}</Modal.Title>
                                        </Modal.Header>
                                        <Modal.Body>
                                            {this.appText('modals.save-changes.body')}
                                        </Modal.Body>
                                        <Modal.Footer>
                                            <Button bsStyle="success" onClick={onConfirm}>{this.appTextCommon('buttons.save-changes')}</Button>
                                            <Button onClick={onCancel}>{this.appTextCommon('buttons.cancel')}</Button>
                                        </Modal.Footer>
                                    </Modal>
                                );
                            }

                            return (<div />);
                        }}
                    </NavigationPrompt>

                    <WhiteBoxPageSection title="">
                        <KendoWrappers.KendoHorizontalForm>
                            <Row className="form-row">
                                { /* 20200202 <Col xs={6} xl={5}> */}
                                <Col xs={7} xl={7}>
                                    <Row>
                                        <Col xs={12}>
                                            <InventoryDetailFormComponentDetailsAssociation sectionTitle={this.appText('association-details')}>
                                                <AssociationDetailsCopyAddress association={assoc} />
                                            </InventoryDetailFormComponentDetailsAssociation>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xs={12}>
                                            {warnCostsDiscrepancy}
                                            <InventoryDetailFormComponentDetailsLeft
                                                appText={(e) => this.appText(e)}
                                                handleFormInputChange={(n, v) => this.handleFormInputChange(n, v, formData)}
                                                handleFormInputChangeEvaluationTier={(n, v) => this.handleEvaluationTierChange(n, v)}
                                                handleFormInputChangeEvaluationVariant={(n, v) => this.handleEvaluationVariantChange(n, v)}
                                                handleFormInputCustomEvaluation={(n, v) => this.handleFormInputCustomEvaluation(n, v)}
                                                handleFormInputChangeUsefulLife={(n, v) => this.handleUsefulLifeChange(n, v)}
                                                handleFormInputCategoryFilter={(n) => this.handleSelectCategoryFilter(n)}
                                                handleCalculate={(e) => this.handleCalculateButton(e)}
                                                handleAddBestExplanation={(e) => this.handleAddBestExplanationButton(e)}
                                                handleAddWorstExplanation={(e) => this.handleAddWorstExplanationButton(e)}
                                                handleSubmitForm={(e) => this.handleSaveButton(e)}
                                                handleNoteDelete={(e, x) => this.handleNoteDelete(e, x)}
                                                handleNoteSave={(e) => this.handleSaveButton(e)}
                                                handleSaveChanges={(e) => this.handleSaveButton(e)}
                                                formData={formData}
                                                defaultInputProps={{ colLabelXs: 4, colInputXs: 8 }}
                                                sectionTitle={this.appText('component-details')}
                                                showComponentLookupEditorButton={isRegionalComponentManager}
                                                {...rest}
                                            />
                                        </Col>
                                    </Row>
                                </Col>

                                { /* 20200202 <Col xs={6} xl={7}> */}
                                <Col xs={5} xl={5}>
                                    <Row>
                                        <Col xs={12}>
                                            { /* <Row>
                                                    <Col xs={12}>
                                                        <InventoryDetailFormComponentDetailsResearch
                                                            appText={(e) => this.appText(e)}
                                                            handleFormInputChange={(n, v) => this.handleFormInputChange(n, v)}
                                                            handleSubmitForm={(e) => this.handleSaveButton(e)}
                                                            handleSaveChanges={(e) => this.handleSaveButton(e)}
                                                            handleNewResearchButton={(e) => this.handleAddNewResearchButton(e)}
                                                            handleDeleteResearchButton={(e, r) => this.handleDeleteResearchButton(e, r)}
                                                            formData={formData}
                                                            inventoryResearch={inventoryResearch}
                                                            numFormChanges={this.getFormChangesCount()}
                                                            defaultInputProps={{ colLabelXs: 4, colInputXs: 8 }}
                                                            sectionTitle={this.appText('research-history')}
                                                            showAdd={this.state.showAddResearch}
                                                        />
                                                    </Col>
                                                </Row> */ }
                                            <Row>
                                                <Col xs={12}>
                                                    <InventoryDetailFormComponentDetailsPictures
                                                        appText={(e) => this.appText(e)}
                                                        handleFormInputChange={(n, v) => this.handleFormInputChange(n, v)}
                                                        handleSubmitForm={(e) => this.handleSaveButton(e)}
                                                        handleSaveChanges={(e) => this.handleSaveButton(e)}
                                                        formData={formData}
                                                        numFormChanges={this.getFormChangesCount()}
                                                        handlePictureSelect={(a, b) => this.handleShowModalSelectPictureButton(a, b)}
                                                        // handlePictureClear={(a, b) => this.handleShowModalSelectPictureButton(a, b) }
                                                        handlePictureClear={(e, fieldName) => { this.handleFormInputChange(_.merge({}, e, { 'target.props.name': fieldName, 'value': '' }), fieldName); }}
                                                        defaultInputProps={{ colLabelXs: 3, colInputXs: 8 }}
                                                        sectionTitle={this.appText('component-pictures')}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={12}>
                                                    <InventoryDetailFormComponentDetailsRight
                                                        appText={(e) => this.appText(e)}
                                                        handleFormInputChange={(n, v) => this.handleFormInputChange(n, v)}
                                                        handleSubmitForm={(e) => this.handleSaveButton(e)}
                                                        handleSaveChanges={(e) => this.handleSaveButton(e)}
                                                        handleNoteDelete={(e, o) => this.handleFormInputChange(e, o)}
                                                        handleNoteSave={(e) => this.handleSaveButton(e)}
                                                        formData={formData}
                                                        numFormChanges={this.getFormChangesCount()}
                                                        defaultInputProps={{ colLabelXs: 4, colInputXs: 8 }}
                                                        sectionTitle={this.appText('component-subgroup')}
                                                    />
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={4}>
                                                    <ButtonGroup>
                                                        <Button bsStyle="primary" onClick={(e) => this.handleShowResearchComponentsModal(e)}>{this.appTextCommon('buttons.show-component-research-modal')}</Button>
                                                    </ButtonGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col xs={12}>
                                                    <InventoryDetailFormComponentDetailsShortcuts
                                                        appText={(e) => this.appText(e)}
                                                        handleFormInputChange={(n, v) => this.handleFormInputChange(n, v)}
                                                        handleSubmitForm={(e) => this.handleSaveButton(e)}
                                                        handleSaveChanges={(e) => this.handleSaveButton(e)}
                                                        handleLinkNavigation={(e, invId, invLink) => this.handlePaginationClick(e, invId, invLink)}
                                                        formData={formData}
                                                        defaultBoxHeight={_.size(invSubgroupItems) * 21}
                                                        // allComponents={allInventory}
                                                        allComponents={invSubgroupItems}
                                                        currentInventoryItem={inv}
                                                        currentInventoryId={Number(inventoryId)}
                                                        numFormChanges={this.getFormChangesCount()}
                                                        defaultInputProps={{ colLabelXs: 4, colInputXs: 8 }}
                                                        sectionTitle={this.appText('shortcuts-to-other-components')}
                                                    />
                                                </Col>
                                            </Row>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </KendoWrappers.KendoHorizontalForm>
                    </WhiteBoxPageSection>
                    {this.renderButtonToolbar(allInventorySorted, inventoryId, associationId)}
                </div>
            );
        } else if (!this.props.loadingBar) {
            page = (<div>
                <p>{this.appText('invalid-inventory-id')}</p>
            </div>);
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = (assocId, invId) => ApiEndpoints.getAssociationInventoryDetailUrl(assocId, invId);

const mapPropsToConfigs = (props) => {
    return [
        ReduxQueries.Queries.getAssociationInventoryDetailRequest(
            getApiUrl(
                _.get(props, 'associationId', 0),
                _.get(props, 'inventoryId', 0)
            ),
            ReduxQueries.PropsAccessToken(props)
        ),
        ReduxQueries.Queries.getLookupsByKeyRequest(
            ApiEndpoints.getLookupsByKeyUrl([
                LK.LookupKeys.Regions,
            ]),
            ReduxQueries.PropsAccessToken(props)
        )
    ];
};

const mapStateToProps = (state, props) => {
    const associationId = props.match.params.associationId;
    const inventoryId = props.match.params.inventoryId;

    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(associationId, inventoryId), state, props, {
        associationId: associationId,
        inventoryId: inventoryId,
        association: state.entities.associationInventoryDetailAssociation,
        associationPhotos: state.entities.associationInventoryDetailAssociationPhotos,
        associationPhotoFolders: state.entities.associationInventoryDetailAssociationPhotoFolders,
        associationPhotosTree: state.entities.associationInventoryDetailAssociationPhotosTree,
        inventory: state.entities.associationInventoryDetailInventory,
        inventoryResearch: state.entities.associationInventoryDetailInventoryResearch,
        allInventory: state.entities.associationInventoryDetailAllInventory,
        replacementCosts: state.entities.associationInventoryDetailReplacementCosts,
        components: state.entities.associationInventoryDetailComponents,
        componentCategories: state.entities.associationInventoryDetailComponentCategories,
        groupComponents: state.entities.associationInventoryDetailGroupComponents,
        costSources: state.entities.associationInventoryDetailCostSources,
        authUserGroups: state.entities.associationInventoryDetailAuthUserGroups,
        userInventoryTemplate: state.entities.associationInventoryDetailUserInventoryTemplate,
        loadingBar: state.loadingBar,
    });
};

const mapDispatchToProps = {
    viewAssociationInventory: Actions.viewAssociationInventorySubgroup,
    searchAssociationInventory: Actions.searchResearchComponentsFilterAssociationId,
    updateAssociationInventory: MutateActions.updateAssociationInventoryMutateAsync,
    newAssociationInventory: MutateActions.newAssociationInventoryMutateAsync,
    deleteAssociationInventory: MutateActions.deleteAssociationInventoryMutateAsync,
    duplicateAssociationInventory: MutateActions.duplicateAssociationInventoryMutateAsync,
    newAssociationInventoryResearch: MutateActions.newAssociationInventoryResearchMutateAsync,
    deleteAssociationInventoryResearch: MutateActions.deleteAssociationInventoryResearchMutateAsync,
    updateComponentReplacementCosts: MutateActions.updateComponentReplacementCostsMutateAsync,
    permAddNewEvalCondition: MutateActions.newStandardEvaluationMutateAsync,
    navigateTo: (path) => push(path)
};

const AssociationInventoryDetailContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(AssociationInventoryDetail);

export default withRouter(AssociationInventoryDetailContainer);



// WEBPACK FOOTER //
// ./src/inventory/detail.tsx